import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { Box, Flex, Grid, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import SelectDate from "components/forms/SelectDate";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon, QuestionMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt, mntType, mntCard, mntPill, mntStepShade } from "theme/maintenanceTokens";
import type { MntStatus } from "theme/maintenanceTokens";
import {
  sourceLabel,
  isPostRfq as woIsPostRfq,
  canMatchWo,
  workOrderDisplayIdentity,
} from "./components/workOrderPresentation";
import { useToast } from "components/toast";
import UserContext from "context/User";

import {
  getWorkOrder,
  updateWorkOrder,
  getPlanGroups,
  assignWorkOrderGroup,
  assignWorkOrderPlan,
  getServicePlans,
  convertWorkOrder,
  reconcileWorkOrderRfq,
  forceRepriceWorkOrderRfq,
  cancelWorkOrder,
  dismissWorkOrderCancellation,
  deleteWorkOrder,
  duplicateWorkOrders,
  getWorkOrderNotes,
  addWorkOrderNote,
  getWorkOrderEvents,
  setWorkOrderMap,
  getInstantEstimateProducts,
  getWorkOrderRfqCandidates,
} from "api/ServicePlans";
import { getQuoteRequest, generateQuoteFromEstimate, updateQuoteRequest } from "api/QuoteRequests";
import { getQuote, getQuotePDF, updateQuote, getProjectQuoteHistory, acceptOnBehalf } from "api/Quotes";
import { getInvoice, getInvoicePDF, emailInvoice, updateInvoice } from "api/Invoices";
import { getMapSnapshots } from "api/MulchMap";
import SnapshotIncludePicker from "components/SnapshotIncludePicker";
import ProjectPhotoIncludePicker from "components/ProjectPhotoIncludePicker";
import CorrigoInvoiceSubmissionPanel from "containers/Invoices/components/CorrigoInvoiceSubmissionPanel";
import SubmitInvoice from "containers/PurchaseOrders/components/SubmitInvoice";
import QuoteDeliveryPanel from "./components/QuoteDeliveryPanel";
import { getProjectNextStatus, updateProject, getProjectPhotos } from "api/Projects";
import { emailPo, getPoPDF, getPurchaseOrder, updatePurchaseOrder, acceptOrDeny as acceptOrDenyPo, acknowledgePo } from "api/PurchaseOrder";
import { getMapsAtLocation } from "api/Locations";
import ProviderPriceControls from "containers/QuoteRequests/components/ProviderPriceControls";

import MatchCandidatesModal from "./components/MatchCandidatesModal";
import SendQuoteModal from "./components/SendQuoteModal";
import TermsEditor from "components/TermsEditor";
import { resolveWoNextAction } from "./components/workOrderNextAction";
import AttachExistingWorkModal from "./components/AttachExistingWorkModal";
import WorkOrderClientStatus from "./WorkOrderClientStatus";
import { getInvoiceStatusDisplay } from "components/ui/InvoiceStatusBadge";

/**
 * Work Order detail — the CASE FILE (mockup 25,
 * docs/maintenance-module/mockups/25-generate-rfq-for-wo.svg).
 *
 * MODEL (RC1, WHATS_LEFT.md): the Work Order is the durable record. You
 * GENERATE an RFQ for it, price it, generate a quote, and send or post
 * it. The RFQ and quote ATTACH BACK here; the WO is never consumed and
 * never becomes an RFQ. No user-facing "convert" language anywhere on
 * this surface. The `convertWorkOrder` route/identifier stays as legacy
 * debt (BUILD CONTRACT) — only the label and the mental model changed.
 *
 * COMPOSITION (rebuild, Session 55 — replaces the read-only intake dump
 * that carried gaps W-a…W-h in design/LIVE_VS_MOCKUP_GAP.md):
 *   W-a  Derived stage strip across the top, driven by the backend's
 *        `effectiveStage`/`stageOrder` (WO_STATUS_DERIVATION.md). Never
 *        hand-set.
 *   W-b  Live pricing block READ FROM THE TRUNK RFQ (RC4). Pricing is
 *        not reimplemented here: the RFQ's estimate already computes
 *        provider, priced lines and totals, so this surfaces them and
 *        links out to `Items.tsx` `ProviderPriceControls` (mockup 24)
 *        for the live controls.
 *   W-c  Guided next-action card whose single button tracks the stage:
 *        Match site -> Generate RFQ -> Generate quote -> Send / Post to
 *        CorrigoPro -> View project / invoice.
 *   W-d  Compact 3-up intake band (edit stays available pre-RFQ behind
 *        an explicit toggle rather than sprawling the page).
 *   W-e  Attached-records rail (RFQ / Quote / Project / Invoice).
 *   W-f  History timeline derived from the loaded record chain. There is
 *        no WO-activity endpoint yet, so this reads received/RFQ/quote/
 *        project/invoice timestamps off the chain the detail load
 *        already carries; a real activity feed is a later backend task.
 *   W-h  Plan / batch / site strip, compact, at the bottom.
 *
 * DATA:
 *   - `getWorkOrder` loads the WO plus convertedRfq + quotes (+ their
 *     project/invoice) and stamps the derived stage. That covers the
 *     strip, the rail and the history.
 *   - The estimate (provider, priced lines, totals) is NOT on that
 *     payload, so the pricing block loads the trunk RFQ itself via
 *     `getQuoteRequest` once `convertedRfq` exists. Same payload the RFQ
 *     page renders, so the numbers here and there cannot drift.
 *   - Generate-quote uses the trunk call the RFQ surface uses:
 *     `generateQuoteFromEstimate({ data: { id: estimate.id },
 *     service: { selectServices: [serviceId] } })`. Offered inline only
 *     when the service to quote is unambiguous; otherwise the button
 *     routes to the RFQ to pick one.
 *
 * All color/type comes from `theme/maintenanceTokens` — no hand-coded
 * hex (MOCKUP_FIDELITY_PASS.md).
 */

/**
 * Route entry for `/maintenance/work-orders/:id`. Role-branches the same
 * way Projects does: staff get the full operator case file below; a
 * client gets the lean read-only job-status view
 * (`WorkOrderClientStatus`). The operator component is not mounted at all
 * for a client, so none of its operator-only fetches (RFQ estimate,
 * provider controls, PO, terms, plan groups) ever fire for a client
 * token. Ownership + field-scoping are enforced server-side on the
 * shared `GET /maintenance/work-orders/:id`.
 */
export default function WorkOrderDetail() {
  const { user } = useContext<any>(UserContext);
  // Until the user resolves, render nothing rather than flashing the
  // operator console at a client (or vice versa).
  if (!user) return null;
  if (user.accessLevel !== "isCompany") return <WorkOrderClientStatus />;
  return <OperatorWorkOrderDetail />;
}

function OperatorWorkOrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [wo, setWo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  // The trunk RFQ, loaded for the pricing block (W-b). Null until the WO
  // has an RFQ, or while it is loading.
  const [rfq, setRfq] = useState<any>(null);
  const [rfqLoading, setRfqLoading] = useState(false);

  // W-k (RC5) — the QUOTE, loaded in full. `getWorkOrder` carries the
  // quote rows off `convertedRfq.quotes` but not their line items, and
  // the client-facing number is exactly what an operator needs to see
  // before sending. Fetched here rather than added to the WO payload,
  // which would fan out a one-to-many for every WO read.
  const [quote, setQuote] = useState<any>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [planGroups, setPlanGroups] = useState<any[] | null>(null);
  const [products, setProducts] = useState<any[] | null>(null);

  const [editingIntake, setEditingIntake] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignGroupId, setAssignGroupId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // KNOWN_ISSUES item 20 - plan move.
  const [showMovePlan, setShowMovePlan] = useState(false);
  const [movePlanId, setMovePlanId] = useState<string | null>(null);
  const [movingPlan, setMovingPlan] = useState(false);
  const [clientPlans, setClientPlans] = useState<any[]>([]);
  const [generatingRfq, setGeneratingRfq] = useState(false);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [showQtyConfirm, setShowQtyConfirm] = useState(false);
  // Attach existing work (2026-10-15): RFQs for this client at this site
  // that no work order owns. When there are any, attaching one is the
  // next step instead of generating a duplicate.
  const [showAttachRfq, setShowAttachRfq] = useState(false);
  const [rfqCandidates, setRfqCandidates] = useState<any[]>([]);
  const [qtyInput, setQtyInput] = useState<string>("");
  const [showSendQuote, setShowSendQuote] = useState(false);
  // Session 65 item 3: which branch the delivery modal opens on. A
  // work_order_fed "Post to Corrigo" / "Accept on behalf" step opens on
  // "corrigo"; a quote_fed "Send quote" step opens on "email". The
  // operator can still toggle inside the modal.
  const [sendQuoteBranch, setSendQuoteBranch] = useState<"email" | "corrigo">("email");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Session 80 — dismissing a client's cancellation request. NOT a
  // cancel: the only thing it changes is that we no longer owe the
  // client an answer.
  const [dismissingRequest, setDismissingRequest] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Session 65 - in-flight guard for the Manage > Duplicate action.
  const [duplicating, setDuplicating] = useState(false);
  // Maps at this WO's site, so the operator can set the quantity from a
  // real measured map instead of re-measuring or guessing.
  const [siteMaps, setSiteMaps] = useState<any[] | null>(null);
  const [applyingMap, setApplyingMap] = useState<string | null>(null);
  // Attach/detach of the persistent map link (separate from applyingMap,
  // which tracks the quantity pull).
  const [linkingMap, setLinkingMap] = useState<string | null>(null);
  const [repricing, setRepricing] = useState(false);
  // N3 (Quote tab) — pre-acceptance "change service type". `svcChangeOpen`
  // toggles the picker; `svcChangeTarget` is the chosen new service id;
  // `svcChanging` guards the in-flight update+select+generate.
  //
  // Consistency fix (Jack): this now behaves like every OTHER change-
  // service-type surface — build a fresh quote at the new service and let
  // it supersede the prior one ONLY at acceptance (via the existing
  // acceptOrDeny cascade), instead of the old shortcut that force-repriced
  // and superseded the not-yet-accepted quote up front. Pre-acceptance
  // there is no project (the project mints at acceptance), so the project-
  // scoped add-quote form cannot be reused; instead we reuse THIS surface's
  // own in-place build primitives: point the WO at the new service, select
  // that service's already-priced estimate line on the RFQ, and generate
  // the fresh quote — the same steps the guided card walks. No new backend,
  // no up-front supersede.
  const [svcChangeOpen, setSvcChangeOpen] = useState(false);
  const [svcChangeTarget, setSvcChangeTarget] = useState<string>("");
  const [svcChanging, setSvcChanging] = useState(false);
  // WO case-file comment log ("Attachments & comms").
  const [woNotes, setWoNotes] = useState<any[] | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  // Recorded activity (who did what). Separate from the chain-derived
  // history below, which is reconstructed from record timestamps.
  const [woEvents, setWoEvents] = useState<any[]>([]);

  // A24 (project tab) — the project's next-status transitions, fetched
  // from the SAME trunk resolver the project Overview uses
  // (`GET /projects/:id/next-status`). Lets an operator advance the
  // project's workflow from the WO without leaving. `projectAdvancing`
  // tracks the in-flight transition button.
  const [projectNextSteps, setProjectNextSteps] = useState<any[] | null>(null);
  const [projectAdvancing, setProjectAdvancing] = useState(false);
  // Session 65 item 7a: in-place scheduled-date editor. When the live
  // project step is the date-bearing "Scheduled" redirect, the operator
  // sets the date here and advances in one action instead of leaving to
  // the project edit screen. `scheduleDateInput` seeds from the project's
  // current scheduleDate on tab load; `scheduleSaving` tracks the write.
  const [scheduleDateInput, setScheduleDateInput] = useState<number | null>(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  // Quote-version history for the project (audit trail): every quote for
  // this project INCLUDING superseded + soft-deleted versions, newest
  // first. Populated when the Project tab opens. Read-only.
  const [quoteHistory, setQuoteHistory] = useState<any[] | null>(null);
  // A25 (PO tab) — in-flight "send to provider" per PO id.
  const [poSending, setPoSending] = useState<string | null>(null);
  // Session 65 item 11: which PO's "Submit provider invoice" modal is
  // open (by PO id). Submitting a provider invoice against an
  // Accepted+ PO is the close-out action that drives it to Billed (via
  // Zoho on approval) and then Closed on payment - the real ladder, not
  // a manual status flip. Done in place so the operator never leaves the
  // WO to close a PO out (RC5).
  const [poInvoiceModalId, setPoInvoiceModalId] = useState<string | null>(null);
  const [invoiceSending, setInvoiceSending] = useState(false);
  // Session 59 (Part B) — full record loads for the terms editor on the
  // Invoice / PO WO tabs. The WO payload carries lightweight nested
  // invoice / PO records (no `resolvedTerms`/`termsOverride`), so the
  // TermsEditor needs the findById-loaded record. Fetched lazily when the
  // tab is active; keyed by id so switching POs reloads.
  const [invoiceFull, setInvoiceFull] = useState<any>(null);
  // Invoice-tab include pickers (mirrors the invoice Overview toolbar):
  // project photos toggled onto the invoice via each photo's
  // `invoiceInclude` flag, and site-map snapshots selected onto the
  // invoice's `mulchMapSnapshotIds`. Lets staff choose what prints on the
  // invoice PDF from the work order, before Send, without opening the
  // invoice record. `projectPhotos` feeds ProjectPhotoIncludePicker;
  // `invoiceSnapshotCount` gates SnapshotIncludePicker (self-hides at 0).
  const [projectPhotos, setProjectPhotos] = useState<any[]>([]);
  const [invoiceSnapshotCount, setInvoiceSnapshotCount] = useState<number>(0);
  const [poFull, setPoFull] = useState<Record<string, any>>({});

  useEffect(() => {
    if (user && user.accessLevel !== "isCompany") {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line
  }, [user?.accessLevel]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Work Order";
  }, []);

  useEffect(() => {
    let alive = true;
    getInstantEstimateProducts().then((rows: any[]) => {
      if (alive) setProducts(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await getWorkOrder({ data: { id } });
    if (res?.id) {
      setWo(res);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // Existing RFQs at this site, only while the WO is matched and has none.
  const woNeedsRfq = !!wo?.id && !wo?.convertedRfq?.id && !!wo?.lcp?.id && wo?.status !== "rejected";
  useEffect(() => {
    if (!woNeedsRfq || !id) {
      setRfqCandidates([]);
      return;
    }
    let alive = true;
    getWorkOrderRfqCandidates(id).then((res: any) => {
      if (alive) setRfqCandidates(Array.isArray(res) ? res : []);
    });
    return () => {
      alive = false;
    };
  }, [woNeedsRfq, id]);

  // Pricing block source (W-b / RC4 / RC5): the price lives on the RFQ,
  // so pull the trunk RFQ payload once the WO points at one. This is
  // also what lets the trunk provider/price controls be MOUNTED here
  // rather than linked to — they take exactly this payload.
  const rfqId = wo?.convertedRfq?.id || null;
  const loadRfq = useCallback(async () => {
    if (!rfqId) {
      setRfq(null);
      return;
    }
    setRfqLoading(true);
    const res = await getQuoteRequest({ data: { id: rfqId } });
    setRfq(res?.id ? res : null);
    setRfqLoading(false);
  }, [rfqId]);

  useEffect(() => {
    loadRfq();
  }, [loadRfq]);

  /**
   * Part 3 (SESSION_56_BRIEF) + Part 1 follow-on — reconcile the RFQ to
   * the work order, once, on load. Two kinds of drift are healed here,
   * both only before a quote exists:
   *   1. SELECTION — Session 55's convert-time selection ran only when the
   *      RFQ was generated, so an RFQ generated before the WO's service
   *      was set carries priced options with none selected. The surface
   *      used to ask the operator to choose a service they had already
   *      chosen on the work order.
   *   2. QUANTITY — an RFQ generated before the WO's quantity was set
   *      keeps the quantity it was priced at (often 1), so the priced
   *      line disagrees with the work order (50 yds on the WO, 1 yd on
   *      the estimate).
   * A single backend reconcile (`reconcileWorkOrderRfq`) fixes both,
   * reusing the Part 1 re-price and its quote hard-gate. The ref keeps it
   * to one attempt per RFQ across the async gap.
   */
  const reconciledRfqRef = React.useRef<string | null>(null);
  useEffect(() => {
    const est = rfq?.estimate;
    if (!rfq?.id || !est) return;
    if (reconciledRfqRef.current === rfq.id) return;
    const quotesOnRfq = Array.isArray(wo?.convertedRfq?.quotes) ? wo.convertedRfq.quotes : [];
    if (quotesOnRfq.length > 0) return; // price already committed to a quote

    const svcs = Array.isArray(est.services) ? est.services : [];

    // Selection drift: WO names a service, nothing selected, exactly one
    // option matches by NAME (the catalog carries duplicate ids per name,
    // so a duplicate name is a real choice, not a default).
    const woName = wo?.service?.name ?? null;
    const selectionNeedsFix =
      !est.selectedService &&
      !!woName &&
      svcs.filter((s: any) => (s?.service?.name ?? s?.name) === woName).length === 1;

    // Quantity drift: the WO carries a quantity the priced line does not
    // reflect. Compare against the selected (or the single / matching)
    // service.
    const woQty =
      wo?.quantity != null && wo?.quantity !== "" ? Number(wo.quantity) : null;
    const pricedSvc =
      est.selectedService ??
      (svcs.length === 1 ? svcs[0] : null) ??
      (woName ? svcs.find((s: any) => (s?.service?.name ?? s?.name) === woName) : null) ??
      null;
    const pricedQty = pricedSvc ? Number(pricedSvc.quantity ?? 0) : null;
    const quantityNeedsFix =
      woQty != null && woQty > 0 && pricedQty != null && pricedQty !== woQty;

    if (!selectionNeedsFix && !quantityNeedsFix) return;

    reconciledRfqRef.current = rfq.id;
    (async () => {
      const res = await reconcileWorkOrderRfq({ data: { id } });
      if (res?.id) {
        loadRfq();
      } else {
        reconciledRfqRef.current = null; // let a later load retry
      }
    })();
    // eslint-disable-next-line
  }, [rfq?.id, rfq?.estimate?.selectedService, wo?.service?.id, wo?.quantity]);

  useEffect(() => {
    const planId = wo?.servicePlan?.id;
    if (!planId) {
      setPlanGroups(null);
      return;
    }
    getPlanGroups({ data: { planId } }).then((rows) =>
      setPlanGroups(Array.isArray(rows) ? rows : []),
    );
  }, [wo?.servicePlan?.id]);

  /**
   * KNOWN_ISSUES item 20 - the plans this WO could move to.
   *
   * Loaded only when the modal opens, not on page load: it is one more
   * request on a screen that already makes several, and nobody needs
   * the list until they are choosing from it.
   *
   * Scoped to the SITE's client rather than the current plan's. A WO
   * sitting under the wrong client's plan is exactly what this feature
   * fixes, so keying off the wrong parent would offer the wrong list.
   */
  useEffect(() => {
    if (!showMovePlan) return;
    const clientId = wo?.lcp?.client?.id || wo?.servicePlan?.client?.id;
    if (!clientId) {
      setClientPlans([]);
      return;
    }
    getServicePlans({ query: { clientId } }).then((rows: any) =>
      setClientPlans(Array.isArray(rows) ? rows : rows?.items ?? []),
    );
  }, [showMovePlan, wo?.lcp?.client?.id, wo?.servicePlan?.client?.id]);

  // Load the saved maps at this WO's site for the "Maps at this site"
  // card. Skipped when the WO has no resolved location.
  useEffect(() => {
    const locId = wo?.lcp?.location?.id ?? null;
    if (!locId) {
      setSiteMaps(null);
      return;
    }
    let alive = true;
    getMapsAtLocation(locId, false).then((rows: any[]) => {
      if (alive) setSiteMaps(Array.isArray(rows) ? rows : []);
    });
    return () => {
      alive = false;
    };
  }, [wo?.lcp?.location?.id]);

  // WO comment log ("Attachments & comms"). Loaded once the WO exists.
  const loadNotes = useCallback(async () => {
    if (!id) return;
    const res = await getWorkOrderNotes({ data: { id } });
    setWoNotes(Array.isArray(res?.items) ? res.items : []);
  }, [id]);

  const loadEvents = useCallback(async () => {
    if (!id) return;
    const rows = await getWorkOrderEvents({ data: { id } });
    setWoEvents(Array.isArray(rows) ? rows : []);
  }, [id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const isPostRfq = woIsPostRfq(wo);
  const canMatch = canMatchWo(wo);
  /**
   * The work order is ALWAYS editable. Its intake facts — problem, SLA
   * dates, NTE, priority, external id — belong to the work order, not to
   * the RFQ, and they keep arriving corrected after an RFQ exists (a
   * revised Corrigo SLA, an NTE the FM raised). Locking the whole form
   * post-RFQ and offering "Edit on RFQ" instead was wrong twice over: it
   * sent the operator away (RC5), and it sent them to a record that does
   * not hold most of these fields. The backend never blocked this — the
   * lock was purely here.
   *
   * The PRICING INPUTS differ in exactly one respect: an RFQ already
   * priced from them does not re-price itself when they change. So they
   * stay editable and the form SAYS so, rather than being disabled.
   */
  const editable = true;
  const pricingLocked = isPostRfq;
  // Part 1 (SESSION_56) — a quote commits the price. Before a quote
  // exists, saving a service-type or quantity change re-prices the RFQ
  // automatically (backend), so the old "does not re-price" warning is
  // wrong pre-quote; it is only correct once a quote has been cut.
  const priceCommitted =
    Array.isArray(wo?.convertedRfq?.quotes) && wo.convertedRfq.quotes.length > 0;

  // Part 5 (PLAN_WO_INHERITANCE.md) — a WO field that differs from its
  // plan's current default reads as an override. Derived by comparison,
  // not stored; an unknown default never marks an override.
  const planDefaults = resolvePlanDefaults(wo?.servicePlan?.serviceTypesConfig);
  const productIsOverride =
    !!planDefaults.productId &&
    !!wo?.product?.id &&
    wo.product.id !== planDefaults.productId;
  const serviceIsOverride =
    !!planDefaults.serviceId &&
    !!wo?.service?.id &&
    wo.service.id !== planDefaults.serviceId;

  const initialValues = useMemo(() => {
    return {
      externalId: wo?.externalId || "",
      name: wo?.name || "",
      productId: wo?.product?.id || "",
      serviceId: wo?.service?.id || "",
      quantity: wo?.quantity != null && wo?.quantity !== "" ? String(wo.quantity) : "",
      sourceType: wo?.sourceType || "manual_upload",
      problemDescription: wo?.problemDescription || "",
      priority: wo?.priority || "",
      nteAmount: wo?.nteAmount != null && wo?.nteAmount !== "" ? String(wo.nteAmount) : "",
      receivedAt: wo?.receivedAt ? moment(wo.receivedAt).unix() : undefined,
      acceptRejectByAt: wo?.acceptRejectByAt ? moment(wo.acceptRejectByAt).unix() : undefined,
      onSiteByAt: wo?.onSiteByAt ? moment(wo.onSiteByAt).unix() : undefined,
      completeByAt: wo?.completeByAt ? moment(wo.completeByAt).unix() : undefined,
    };
  }, [wo]);

  const validate = (values: any) => {
    const errors: any = {};
    if (!values.receivedAt) errors.receivedAt = "When did this come in?";
    if (values.quantity !== "" && values.quantity != null && Number(values.quantity) < 0) {
      errors.quantity = "Must be zero or more.";
    }
    if (values.nteAmount !== "" && values.nteAmount != null && Number(values.nteAmount) < 0) {
      errors.nteAmount = "Must be zero or more.";
    }
    return errors;
  };

  // Patch only what changed; null clears a nullable field.
  const submit = async (values: any) => {
    setSaving(true);

    const update: any = {};
    const orig = initialValues;

    if (values.externalId !== orig.externalId) {
      update.externalId = values.externalId?.trim() ? values.externalId.trim() : null;
    }
    if (values.name !== orig.name) {
      update.name = values.name?.trim() ? values.name.trim() : null;
    }
    if (values.productId !== orig.productId) {
      update.productId = values.productId || null;
    }
    if (values.serviceId !== orig.serviceId) {
      update.serviceId = values.serviceId || null;
    }
    if (values.quantity !== orig.quantity) {
      update.quantity =
        values.quantity !== "" && values.quantity != null ? Number(values.quantity) : null;
    }
    if (values.problemDescription !== orig.problemDescription) {
      update.problemDescription = values.problemDescription?.trim() || null;
    }
    if (values.priority !== orig.priority) {
      update.priority = values.priority?.trim() ? values.priority.trim() : null;
    }
    // KNOWN_ISSUES #12 — the intake stamp is correctable. Never sent as
    // null: the column is NOT NULL and the select cannot be cleared, so a
    // change is always to another real enum member.
    if (values.sourceType && values.sourceType !== orig.sourceType) {
      update.sourceType = values.sourceType;
    }
    if (values.nteAmount !== orig.nteAmount) {
      update.nteAmount =
        values.nteAmount !== "" && values.nteAmount != null ? Number(values.nteAmount) : null;
    }
    if (values.receivedAt !== orig.receivedAt) {
      update.receivedAt = values.receivedAt
        ? moment.unix(values.receivedAt).toISOString()
        : undefined;
    }
    for (const k of ["acceptRejectByAt", "onSiteByAt", "completeByAt"] as const) {
      if (values[k] !== orig[k]) {
        update[k] = values[k] ? moment.unix(values[k]).toISOString() : null;
      }
    }

    if (Object.keys(update).length === 0) {
      setSaving(false);
      setEditingIntake(false);
      return;
    }

    const res = await updateWorkOrder({ data: { id }, update });
    setSaving(false);
    if (res?.id) {
      setWo(res);
      setEditingIntake(false);
      toast.add("Work order saved.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message || "Error saving work order.", "var(--theme-ui-colors-danger)");
    }
  };

  const submitAssign = async () => {
    setAssigning(true);
    const res = await assignWorkOrderGroup({ data: { id, groupId: assignGroupId } });
    setAssigning(false);
    if (res?.id) {
      toast.add(
        assignGroupId ? "Work order assigned to batch." : "Work order removed from batch.",
        "var(--theme-ui-colors-success)",
      );
      setShowAssign(false);
      load();
    } else {
      toast.add(res?.message || "Error updating batch.", "var(--theme-ui-colors-danger)");
    }
  };

  /**
   * KNOWN_ISSUES item 20 - move this work order to another plan.
   *
   * The batch is cleared server-side, always: a batch belongs to one
   * plan, so it cannot survive the move. The toast says so rather than
   * letting the operator find an empty batch field later.
   *
   * The old plan KEEPS the site on its roster - un-enrolment is a
   * product decision, and the site may carry other work there. The
   * response tells us whether that happened, and we say it out loud.
   */
  const submitMovePlan = async () => {
    setMovingPlan(true);
    const res: any = await assignWorkOrderPlan({
      data: { id, servicePlanId: movePlanId },
    });
    setMovingPlan(false);

    if (res?.workOrder?.id) {
      const hadBatch = !!wo?.group?.id;
      const parts = [
        movePlanId ? "Work order moved." : "Work order removed from its plan.",
      ];
      if (hadBatch) parts.push("Its batch was cleared - batches belong to one plan.");
      if (res?.previousPlanStillEnrolled) {
        parts.push("The site is still on the previous plan's roster.");
      }
      toast.add(parts.join(" "), "var(--theme-ui-colors-success)");
      setShowMovePlan(false);
      load();
    } else {
      toast.add(
        res?.message || "Error moving work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Cancel (soft) with cascade — keeps the record + history but tears down
  // the live chain (project -> Cancelled, quotes superseded, unsent POs
  // soft-deleted, unpaid invoices archived) and releases the batch roster
  // row. Returns a summary of what was auto-handled vs what still needs a
  // human; we set the returned WO and surface the needs-human counts.
  //
  // Session 79: sent POs are no longer a to-do item in this toast. The
  // server now emails the provider, notes the PO and posts to Slack, so
  // the toast reports what HAPPENED. The failure branch is the one that
  // matters and it is shown in the danger colour even though the cancel
  // itself succeeded: a cancelled job the provider does not know about
  // is not a success the operator should scroll past.
  const submitCancel = async () => {
    setCancelling(true);
    const res: any = await cancelWorkOrder({ data: { id } });
    setCancelling(false);
    setShowCancelConfirm(false);
    if (res?.cancelled || res?.alreadyCancelled) {
      if (res?.alreadyCancelled) {
        toast.add("Work order was already cancelled.", "var(--theme-ui-colors-success)");
      } else {
        const notified = Number(res?.providerNoticesSent ?? 0);
        const notifyFailed = Number(res?.providerNoticesFailed ?? 0);
        const finance = Array.isArray(res?.needsFinanceReview) ? res.needsFinanceReview.length : 0;

        if (notifyFailed > 0) {
          // The provider is dispatched and does not know. Nothing else in
          // this summary outranks that, so it gets the whole toast.
          toast.add(
            `Work order cancelled, but we could not reach the provider on ${notifyFailed} sent PO${
              notifyFailed === 1 ? "" : "s"
            } — call them.`,
            "var(--theme-ui-colors-danger)",
          );
        } else {
          const parts: string[] = [];
          if (notified > 0)
            parts.push(
              `The provider was emailed about ${notified} sent PO${notified === 1 ? "" : "s"}.`,
            );
          if (finance > 0)
            parts.push(
              `${finance} paid/partial invoice${
                finance === 1 ? "" : "s"
              } need finance to review.`,
            );
          toast.add(
            parts.length
              ? `Work order cancelled. ${parts.join(" ")}`
              : "Work order cancelled. The project, quotes, unsent POs and unpaid invoices were reconciled.",
            "var(--theme-ui-colors-success)",
          );
        }
      }
      if (res?.workOrder?.id) {
        setWo(res.workOrder);
      } else {
        load();
      }
    } else {
      toast.add(
        res?.message || "Could not cancel the work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Session 80 — staff decline a client's cancellation request. Staff
  // APPROVING is `submitCancel` above: the cascade clears the request as
  // part of its transaction, so there is no separate approve call.
  const submitDismissCancellationRequest = async () => {
    if (dismissingRequest) return;
    setDismissingRequest(true);
    const res: any = await dismissWorkOrderCancellation({ data: { id } });
    setDismissingRequest(false);
    if (res?.id) {
      setWo(res);
      toast.add(
        "Cancellation request dismissed. The work order continues.",
        "var(--theme-ui-colors-success)",
      );
      return;
    }
    toast.add(
      res?.message || "Could not dismiss the cancellation request.",
      "var(--theme-ui-colors-danger)",
    );
  };

  // Delete (hard) — only offered before an RFQ exists; the server guards
  // it too. Removes the WO row and returns to the queue.
  const submitDelete = async () => {
    setDeleting(true);
    const res = await deleteWorkOrder({ data: { id } });
    setDeleting(false);
    if (res?.success) {
      toast.add("Work order deleted.", "var(--theme-ui-colors-success)");
      navigate("/maintenance/work-orders");
    } else {
      setShowDeleteConfirm(false);
      toast.add(
        res?.message || "Could not delete the work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Session 65 - duplicate THIS work order (clone-for-revisit) from the
  // Manage menu. Backend copies the identifying inputs (plan, site,
  // batch, product, service, quantity, name) into a fresh WO that
  // re-enters the pipeline at intake; the trunk chain and Corrigo WO#
  // are intentionally not carried. Lands the operator on the new WO. The
  // route takes an array, so the same path serves a future multi-select
  // from the list; here it clones the one being viewed.
  const submitDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    const res = await duplicateWorkOrders({ data: { ids: [id as string] } });
    setDuplicating(false);
    const newId = res?.created?.[0]?.id;
    if (res?.success && newId) {
      toast.add("Work order duplicated.", "var(--theme-ui-colors-success)");
      navigate(`/maintenance/work-orders/${newId}`);
    } else {
      toast.add(
        res?.message || "Could not duplicate the work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Generate the RFQ FOR this work order (RC1). Keeps the existing
  // `convertWorkOrder` route; the operator confirms the quantity that
  // prices it first, then the RFQ is created, priced and attached back.
  const openGenerateRfq = () => {
    setQtyInput(wo?.quantity != null && wo?.quantity !== "" ? String(wo.quantity) : "");
    setShowQtyConfirm(true);
  };

  const confirmGenerateRfq = async () => {
    const qty = Number(qtyInput);
    if (!qtyInput || isNaN(qty) || qty <= 0) {
      toast.add(
        "Enter a quantity greater than zero to generate the RFQ.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }
    setGeneratingRfq(true);
    const overrides: any = { quantity: qty };
    if (wo?.product?.id) overrides.productId = wo.product.id;
    const res = await convertWorkOrder({ data: { id }, overrides });
    setGeneratingRfq(false);
    if (res?.rfqId) {
      setShowQtyConfirm(false);
      toast.add(
        res?.seeded
          ? "RFQ generated and priced. It is attached to this work order."
          : "RFQ generated and attached. Open it to select services and pricing.",
        "var(--theme-ui-colors-success)",
      );
      load();
      // Session 65 item 2: land on the pricing tab, where the freshly
      // generated RFQ is priced / its service option chosen.
      setActiveTab("pricing");
    } else if (res?.error === "Already converted" && res?.existingRfqId) {
      setShowQtyConfirm(false);
      toast.add("An RFQ already exists for this work order.", "var(--theme-ui-colors-warning)");
      load();
    } else {
      toast.add(
        res?.message || "Could not generate the RFQ for this work order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  const woLocationId = wo?.lcp?.location?.id || null;
  const planClientId = wo?.servicePlan?.client?.id || wo?.lcp?.client?.id || null;
  // Measuring a site is useful at ANY stage: the quantity stays
  // editable post-RFQ, and a crew often measures after the RFQ exists to
  // check the number that was quoted. Gate on having a location, not on
  // the stage.
  const canMeasure = !!woLocationId;
  const goToCalculator = () => {
    if (!woLocationId) {
      toast.add(
        "Match this work order to a location first, then measure it.",
        "var(--theme-ui-colors-warning)",
      );
      return;
    }
    const params = new URLSearchParams({ returnToWo: String(id) });
    if (planClientId) params.set("clientId", String(planClientId));
    navigate(`/mulch-calculator/${woLocationId}/edit?${params.toString()}`);
  };

  // Associate a measured map with the work order by pulling its total
  // into the WO quantity. There is no persistent WO↔map FK yet (that
  // needs a migration); this drives the number the RFQ prices from,
  // which is the association that matters operationally. Pre-quote the
  // save re-prices the RFQ (Part 1); post-quote it updates the WO record
  // only.
  const useMapTotal = async (m: any) => {
    const total = Number(m?.totalCuYd);
    if (!Number.isFinite(total) || total <= 0) {
      toast.add("That map has no measured volume yet.", "var(--theme-ui-colors-warning)");
      return;
    }
    const qty = Math.round(total);
    setApplyingMap(m.mapGroupId);
    const res = await updateWorkOrder({ data: { id }, update: { quantity: qty } });
    if (res?.id) {
      // Remember WHICH map fed the quantity, so the case file can show
      // what it was priced from. Pins the map's current version row.
      const withMap = await setWorkOrderMap({ data: { id }, mulchMapId: m.currentMapId });
      setApplyingMap(null);
      setWo(withMap?.id ? withMap : res);
      loadRfq();
      toast.add(
        `Quantity set to ${qty} from ${m.name || "the map"}, and linked to this work order.`,
        "var(--theme-ui-colors-success)",
      );
    } else {
      setApplyingMap(null);
      toast.add(res?.message || "Could not apply the map total.", "var(--theme-ui-colors-danger)");
    }
  };

  // Attach a map to the WO without touching the quantity, or clear the
  // link (mulchMapId=null). Used by the "Link"/"Unlink" affordances on
  // the Maps card, so an operator can record the map that priced the WO
  // even when the quantity is already right.
  const setMapForWo = async (mulchMapId: string | null, busyKey: string) => {
    setLinkingMap(busyKey);
    const res = await setWorkOrderMap({ data: { id }, mulchMapId });
    setLinkingMap(null);
    if (res?.id) {
      setWo(res);
      toast.add(
        mulchMapId ? "Map linked to this work order." : "Map unlinked.",
        "var(--theme-ui-colors-success)",
      );
    } else {
      toast.add(res?.message || "Could not update the map link.", "var(--theme-ui-colors-danger)");
    }
  };

  // "Re-price RFQ to match" - the resolve button on the service-mismatch
  // warning. An intentional service change on a not-yet-accepted quote
  // cascades, but it MINTS THE NEXT VERSION rather than blanking the
  // record: the backend re-prices the estimate and leaves the prior quote
  // live; here we then generate the next quote version in place (the same
  // generateQuoteFromEstimate the change-service path uses), and the prior
  // version is superseded only when the new one is ACCEPTED (acceptOrDeny).
  // This is the versioning model and matches the change-service behavior;
  // the old supersede-and-reset shortcut is gone.
  const doForceReprice = async () => {
    setRepricing(true);
    const res = await forceRepriceWorkOrderRfq({ data: { id } });
    if (!res?.id) {
      setRepricing(false);
      toast.add(res?.message || "Could not re-price the RFQ.", "var(--theme-ui-colors-danger)");
      return;
    }

    // The estimate is now re-priced to the WO's service. Reload it, find
    // the priced line for the WO's service, select it, and generate the
    // next version. Mirrors changeServiceTypePreAcceptance's tail.
    const targetRfqId = res.convertedRfq?.id ?? rfq?.id;
    const rfqRes: any = await getQuoteRequest({ data: { id: targetRfqId } });
    const freshEstimate = rfqRes?.estimate ?? estimate;
    const freshServices: any[] = Array.isArray(freshEstimate?.services)
      ? freshEstimate.services
      : [];
    const woServiceId = res.service?.id ?? wo?.service?.id ?? null;
    const targetLine =
      freshServices.find((s: any) => (s?.service?.id ?? null) === woServiceId) ??
      freshServices.find((s: any) => s?.selected) ??
      null;

    if (!freshEstimate?.id || !targetLine?.id) {
      // Re-priced, but no priced line resolved to generate from - leave the
      // prior quote live and route the operator to Pricing rather than
      // guessing.
      setRepricing(false);
      toast.add(
        "RFQ re-priced to match the work order. Generate the next quote version from the Pricing tab.",
        "var(--theme-ui-colors-success)",
      );
      await load();
      loadRfq();
      loadQuote();
      return;
    }

    const sel = await updateQuoteRequest({
      data: { id: targetRfqId },
      update: { selectServices: [targetLine.id] },
    });
    if (!sel?.id) {
      setRepricing(false);
      toast.add(sel?.message || "Could not select the re-priced service.", "var(--theme-ui-colors-danger)");
      await load();
      loadRfq();
      loadQuote();
      return;
    }

    const gen = await generateQuoteFromEstimate({
      data: { id: freshEstimate.id },
      service: { selectServices: [targetLine.id] },
    });
    setRepricing(false);
    if (gen?.id) {
      toast.add(
        "RFQ re-priced and the next quote version generated. Accepting it will supersede the prior quote.",
        "var(--theme-ui-colors-success)",
      );
    } else {
      toast.add(
        gen?.message ||
          "RFQ re-priced, but the next quote version could not be generated. Try Generate quote from the Pricing tab.",
        "var(--theme-ui-colors-danger)",
      );
    }
    await load();
    loadRfq();
    // Seed the new quote by id (on demand, no refresh) when generated;
    // else fall back to the newest-quote reload.
    if (gen?.id) loadQuoteById(gen.id);
    else loadQuote();
  };

  // N3 (Quote tab) — pre-acceptance change of service type, made
  // consistent with the project-tab / post-accept surfaces (Jack): build a
  // fresh quote at the new service; the prior quote is superseded only
  // when the new one is ACCEPTED (the existing acceptOrDeny cascade), not
  // up front. Steps, all in place on this surface and reusing its own
  // primitives:
  //   1) point the WO at the new catalog service (updateWorkOrder), so the
  //      work order record reflects the change.
  //   2) find that service's already-priced line on the RFQ estimate and
  //      SELECT it (updateQuoteRequest selectServices), so pricing resolves
  //      to the new service. The estimate prices product x available
  //      services, so the target line normally already exists; if it does
  //      not (nothing priced for this service), we stop and point the
  //      operator at the RFQ to (re)price, rather than superseding blindly.
  //   3) generate the fresh quote from that estimate line
  //      (generateQuoteFromEstimate) — the same call the guided "generate
  //      quote" action makes. It attaches back to the WO; the stage strip
  //      re-derives. No project exists yet, so nothing to reconcile; the
  //      prior quote retires at acceptance.
  const changeServiceTypePreAcceptance = async () => {
    const targetServiceId = svcChangeTarget;
    if (!targetServiceId || targetServiceId === (wo?.service?.id || "")) return;
    if (!rfq?.id || !estimate?.id) {
      toast.add(
        "This work order has no priced RFQ yet. Generate the RFQ first, then change the service.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }

    // The estimate line priced for the target service (match by catalog
    // service id, falling back to name for catalogs with duplicate ids).
    const targetName = (
      (products || []).find((p: any) => p.id === wo?.product?.id)?.services ?? []
    ).find((s: any) => s.id === targetServiceId)?.name;
    const targetLine =
      estimateServices.find((s: any) => (s?.service?.id ?? null) === targetServiceId) ??
      (targetName
        ? estimateServices.find(
            (s: any) => (s?.service?.name ?? s?.name) === targetName,
          )
        : null);

    setSvcChanging(true);

    // 1) point the WO at the new service.
    const upd = await updateWorkOrder({
      data: { id },
      update: { serviceId: targetServiceId },
    });
    if (!upd?.id) {
      setSvcChanging(false);
      toast.add(upd?.message || "Could not update the service type.", "var(--theme-ui-colors-danger)");
      return;
    }

    // 2) if the new service is not yet priced on the estimate, send the
    //    operator to the RFQ to (re)price it rather than superseding the
    //    current quote with nothing to replace it.
    if (!targetLine?.id) {
      setSvcChanging(false);
      setSvcChangeOpen(false);
      setSvcChangeTarget("");
      toast.add(
        "Service updated on the work order. It is not priced on the RFQ yet — open the RFQ to price the new service, then generate the quote.",
        "var(--theme-ui-colors-success)",
      );
      await load();
      loadRfq();
      return;
    }

    // Select the target line so the RFQ / pricing block resolves to it.
    const sel = await updateQuoteRequest({
      data: { id: rfq.id },
      update: { selectServices: [targetLine.id] },
    });
    if (!sel?.id) {
      setSvcChanging(false);
      toast.add(sel?.message || "Could not select the new service pricing.", "var(--theme-ui-colors-danger)");
      await load();
      loadRfq();
      return;
    }

    // 3) generate the fresh quote at the new service.
    const gen = await generateQuoteFromEstimate({
      data: { id: estimate.id },
      service: { selectServices: [targetLine.id] },
    });
    setSvcChanging(false);
    if (gen?.id) {
      setSvcChangeOpen(false);
      setSvcChangeTarget("");
      toast.add(
        "New quote generated for the changed service. Accepting it will supersede the prior quote.",
        "var(--theme-ui-colors-success)",
      );
      await load();
      loadRfq();
      // On demand, no refresh: seed the new quote by id.
      loadQuoteById(gen.id);
    } else {
      // WO service + selection updated, but generate failed; leave the
      // prior quote intact and let the operator generate from Pricing.
      toast.add(
        gen?.message ||
          "The service was updated but the new quote could not be generated. Try Generate quote from the Pricing tab.",
        "var(--theme-ui-colors-danger)",
      );
      await load();
      loadRfq();
      loadQuote();
    }
  };

  // Add a comment to the WO case-file log.
  const submitNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setAddingNote(true);
    const res = await addWorkOrderNote({ data: { id }, note: text });
    setAddingNote(false);
    if (res?.id) {
      setNoteDraft("");
      loadNotes();
      loadEvents();
    } else {
      toast.add(res?.message || "Could not add the note.", "var(--theme-ui-colors-danger)");
    }
  };

  // A24 (project tab) — resolve the WO's project id off the loaded chain
  // (RFQ.project, else newest quote's project). Same project the
  // Attached-records rail and the project bar read.
  const attachedProjectId =
    wo?.convertedRfq?.project?.id ??
    (Array.isArray(wo?.convertedRfq?.quotes)
      ? wo.convertedRfq.quotes
          .map((q: any) => q?.project?.id)
          .find((pid: any) => !!pid)
      : null) ??
    null;
  const attachedProjectStatusId =
    wo?.convertedRfq?.project?.status?.id ??
    (Array.isArray(wo?.convertedRfq?.quotes)
      ? wo.convertedRfq.quotes
          .map((q: any) => q?.project?.status?.id)
          .find((sid: any) => !!sid)
      : null) ??
    null;

  // Fetch the project's next-status transitions from the trunk resolver
  // the project Overview uses — keyed off the project's CURRENT status id
  // (that is what `/projects/:id/next-status` expects, per Overview). No
  // fork: the WO tab reads the same transitions and drives the same
  // `updateProject` write.
  const loadProjectNextSteps = useCallback(async () => {
    if (!attachedProjectStatusId) {
      setProjectNextSteps(null);
      return;
    }
    const res = await getProjectNextStatus({ data: { id: attachedProjectStatusId } });
    setProjectNextSteps(Array.isArray(res) ? res : null);
  }, [attachedProjectStatusId]);

  useEffect(() => {
    loadProjectNextSteps();
  }, [loadProjectNextSteps]);

  // Load the project's quote-version history when the Project tab is open.
  // Deferred to tab-open (not part of the WO payload) because it is an
  // audit-trail read a handful of operators consult, not something every
  // WO load needs. Keyed on the project id + active tab.
  const loadQuoteHistory = useCallback(async () => {
    if (!attachedProjectId) {
      setQuoteHistory(null);
      return;
    }
    const rows = await getProjectQuoteHistory({ projectId: attachedProjectId });
    setQuoteHistory(Array.isArray(rows) ? rows : []);
  }, [attachedProjectId]);

  // Accept a pending quote version ON BEHALF of the client, in place on
  // the WO (F2). For Corrigo-managed clients the FM approves in
  // CorrigoPro; staff records that here without opening the quote. Reuses
  // the same acceptOnBehalf path as the quote page (one acceptance code
  // path), seeding externalWorkOrderNumber from the WO's external id.
  // On success the prior version supersedes via the existing acceptOrDeny
  // cascade, so we reload BOTH the history and the WO so the stage strip,
  // rail, and this list all re-derive.
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | null>(null);
  const acceptQuoteOnBehalf = async (quoteId: string) => {
    setAcceptingQuoteId(quoteId);
    const res: any = await acceptOnBehalf({
      data: { id: quoteId },
      externalWorkOrderNumber: wo?.externalId?.trim() || "N/A",
    });
    if (res?.success) {
      toast.add("Quote accepted on behalf of client.", "var(--theme-ui-colors-success)");
      await loadQuoteHistory();
      await load();
      // Session 65 item 2 (+ root cause A): acceptance turns the quote
      // into scheduled work, so jump to the PROJECT tab immediately
      // rather than leaving the operator on the quote and defaulting to
      // PO on the next manual reload.
      setActiveTab("project");
    } else {
      toast.add(res?.message || "Could not accept the quote.", "var(--theme-ui-colors-danger)");
    }
    setAcceptingQuoteId(null);
  };

  // Advance the project one step (A24). TRANSITION writes the new status
  // to the trunk project via `updateProject` (the exact call the project
  // Overview makes), then reloads the WO so the bar strip + rail + this
  // tab all re-derive from the live project.
  //
  // Session 65 item 7b: a REDIRECT transition (e.g. "Add invoice" ->
  // `/projects/:id/add-invoice/:quoteId`) used to be flattened to the
  // project overview, discarding the transition's real target and
  // stranding the operator off the WO. Now it navigates to the
  // transition's OWN `actionUrl` (substituting `:id`/`:quoteId` like the
  // trunk Overview does) and appends `?returnToWo=<wo.id>` so the target
  // flow returns to this work order (the add-invoice editor reads the
  // param and lands back on the WO invoice tab). The scheduled-date
  // redirect is intercepted BEFORE here by the in-place editor (7a), so
  // this path carries the remaining redirects (add-invoice, etc.).
  const advanceProject = async (step: any) => {
    const t = step?.transition;
    if (!t) return;
    if (t.actionType === "REDIRECT") {
      if (!attachedProjectId) return;
      const acceptedQuoteId =
        (Array.isArray(wo?.convertedRfq?.quotes)
          ? wo.convertedRfq.quotes.find((q: any) => Number(q?.status) === 1)?.id
          : null) ??
        wo?.convertedRfq?.project?.quote?.id ??
        null;
      let url: string = t.actionUrl || `/projects/${attachedProjectId}?tab=overview`;
      url = url.replace(":id", String(attachedProjectId));
      if (url.includes(":quoteId") && acceptedQuoteId) {
        url = url.replace(":quoteId", String(acceptedQuoteId));
      }
      // Only append the return target when the URL resolved cleanly (no
      // leftover placeholder) so we never navigate to a broken path.
      if (!url.includes(":")) {
        const sep = url.includes("?") ? "&" : "?";
        url = `${url}${sep}returnToWo=${id}`;
      }
      navigate(url);
      return;
    }
    if (t.actionType !== "TRANSITION" || !attachedProjectId) return;
    setProjectAdvancing(true);
    const res = await updateProject({
      data: { id: attachedProjectId },
      update: { status: { id: t.toStatusId } },
    });
    setProjectAdvancing(false);
    if (res?.id) {
      toast.add(
        `Project status set to ${res.status?.name ?? "the next step"}.`,
        "var(--theme-ui-colors-success)",
      );
      await load();
    } else {
      toast.add(
        res?.message || "Could not advance the project.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Session 65 item 7a: is this next-step the date-bearing "Scheduled"
  // redirect? The seeded transition rows are sandbox-only (unverifiable
  // from prod MCP), so key off the transition's OWN fields, not a
  // hardcoded status id: a REDIRECT whose action text mentions scheduling
  // OR whose target url is the project edit screen (where the date is set
  // on the trunk). Anything else stays an ordinary Advance / redirect.
  const isScheduleStep = (step: any): boolean => {
    const t = step?.transition;
    if (!t || t.actionType !== "REDIRECT") return false;
    const text = String(t.actionText ?? "").toLowerCase();
    const url = String(t.actionUrl ?? "").toLowerCase();
    return /schedul/.test(text) || /\/edit\b/.test(url);
  };

  // Is this project step the "add/send invoice" step, AND does the invoice
  // already exist? On a maintenance plan the invoice is raised when the
  // project is created (see the Attached-records rail: the invoice reads
  // "Created" at project-complete), so the project's invoice step is not a
  // CREATE - it is "go send the invoice that already exists." When both
  // are true we relabel to "Send invoice" and just switch to the invoice
  // tab (no add-invoice redirect / create form). If somehow no invoice
  // exists yet, this returns false and the normal add-invoice redirect
  // still runs.
  const isSendInvoiceStep = (step: any): boolean => {
    const t = step?.transition;
    if (!t) return false;
    const text = String(t.actionText ?? "").toLowerCase();
    const url = String(t.actionUrl ?? "").toLowerCase();
    const looksLikeInvoice = /invoice/.test(text) || /add-invoice/.test(url);
    return looksLikeInvoice && !!attachedInvoice?.id;
  };

  // Save the scheduled date AND advance to the Scheduled status in one
  // write (RC5 - the operator never leaves the WO). Mirrors the trunk
  // project edit's scheduleDate field + status advance, collapsed here.
  const saveScheduledDate = async (step: any) => {
    const t = step?.transition;
    if (!t || !attachedProjectId) return;
    if (!scheduleDateInput) {
      toast.add("Choose a scheduled date first.", "var(--theme-ui-colors-warning)");
      return;
    }
    setScheduleSaving(true);
    const res = await updateProject({
      data: { id: attachedProjectId },
      update: {
        // SelectDate holds a Unix timestamp; the backend DTO wants a date
        // STRING (`@IsString`). Format exactly as the trunk project edit
        // does (`moment.unix(...).format("YYYY-MM-DD HH:mm:ss")`) - sending
        // the raw number was the "scheduleDate must be a string" error.
        scheduleDate: moment.unix(scheduleDateInput).format("YYYY-MM-DD HH:mm:ss"),
        ...(t.toStatusId ? { status: { id: t.toStatusId } } : {}),
      },
    });
    setScheduleSaving(false);
    if (res?.id) {
      toast.add(
        `Scheduled${res.status?.name ? ` - ${res.status.name}` : ""}.`,
        "var(--theme-ui-colors-success)",
      );
      await load();
      setActiveTab("project");
    } else {
      toast.add(
        res?.message || "Could not set the scheduled date.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Send a PO to its provider (A25). The one staff-side trunk action that
  // moves a New(0) PO to Sent(1); mirrors the PO view's "send by email".
  // Provider acceptance and payments stay on the PO record itself.
  // Session 65 item 6b: branch on the api's explicit `success` flag, NOT
  // on the absence of a `message` (a successful send-by-email response
  // carries a message, which used to trip the danger branch and show a
  // RED toast that read like an error though the PO went out). On success
  // reload AND land on the PO tab so the sent state is in view.
  const sendPo = async (po: any) => {
    if (!po?.id) return;
    setPoSending(po.id);
    const res = await emailPo({ data: { id: po.id } });
    setPoSending(null);
    if (res?.success) {
      toast.add("Purchase order sent to the provider.", "var(--theme-ui-colors-success)");
      await load();
      setActiveTab("po");
    } else {
      toast.add(
        res?.message || "Could not send the purchase order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Session 65 item 4 - accept a SENT PO on the provider's behalf, in
  // place on the WO PO tab (the same acceptOrDeny cascade the PO page
  // runs). Corrigo/FM-managed flows and phone confirmations mean staff
  // routinely record the provider's acceptance themselves. The backend
  // gates status->2 (Accepted) behind terms acknowledgment, so we
  // acknowledge first (idempotent) and then accept - one click for the
  // operator. On success the PO moves to Accepted and "Submit provider
  // invoice" becomes the next action on the same row.
  const acceptPoOnBehalf = async (po: any) => {
    if (!po?.id) return;
    setPoSending(po.id);
    // Clear the acknowledgment gate first; idempotent, safe to always call.
    await acknowledgePo({ data: { id: po.id } });
    const res = await acceptOrDenyPo({ data: { id: po.id }, update: { status: 2 } });
    setPoSending(null);
    if (res?.success) {
      toast.add("Purchase order accepted on the provider's behalf.", "var(--theme-ui-colors-success)");
      await load();
      setActiveTab("po");
    } else {
      toast.add(
        res?.message || "Could not accept the purchase order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Session 65 item 4 - deny/reject a SENT PO in place (status->4). No
  // acknowledgment gate on deny (a wrong-provider / out-of-area case must
  // fire without reading terms), mirroring the PO page.
  const denyPo = async (po: any) => {
    if (!po?.id) return;
    setPoSending(po.id);
    const res = await acceptOrDenyPo({ data: { id: po.id }, update: { status: 4 } });
    setPoSending(null);
    if (res?.success) {
      toast.add("Purchase order denied.", "var(--theme-ui-colors-success)");
      await load();
      setActiveTab("po");
    } else {
      toast.add(
        res?.message || "Could not deny the purchase order.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  // Send the invoice to the client (Invoice tab). Reuses the same
  // /invoices/:id/send-by-email endpoint the invoice Overview uses;
  // provider acceptance and payments stay on the invoice record.
  // Session 65 item 6a: branch on the api's explicit `success` flag, not
  // on the absence of a `message`. The backend returns success for BOTH a
  // real send AND a plan-suppressed send (a Corrigo/work_order_fed plan
  // deliberately does NOT email the client), so a suppression is NOT an
  // error — surface it as an informational note, not a red toast. On a
  // real send, land on the invoice tab so the Sent state is in view.
  const sendInvoice = async (inv: any) => {
    if (!inv?.id) return;
    setInvoiceSending(true);
    const res = await emailInvoice({ data: { id: inv.id } });
    setInvoiceSending(false);
    const suppressed =
      res?.success && String(res?.message ?? "").toLowerCase().includes("suppress");
    if (res?.success && !suppressed) {
      toast.add("Invoice sent to the client.", "var(--theme-ui-colors-success)");
      await load();
      setActiveTab("invoice");
    } else if (suppressed) {
      // Corrigo plan: the client is not emailed. Point staff at the
      // Corrigo submission path below rather than reporting a failure.
      toast.add(
        "This plan does not email the client. Submit the invoice through CorrigoPro below.",
        "var(--theme-ui-colors-warning)",
      );
      await load();
    } else {
      toast.add(
        res?.message || "Could not send the invoice.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  /* ── pricing derivation (W-b) ─────────────────────────────────── */

  // The service the pricing block reads. `selectedService` is the
  // committed one; a single unselected service is equally unambiguous.
  // More than one and the operator has a choice to make on the RFQ, so
  // this surface shows the options count and links out rather than
  // guessing a total.
  const estimate = rfq?.estimate ?? null;
  const estimateServices: any[] = Array.isArray(estimate?.services) ? estimate.services : [];

  // Part 3 (SESSION_56_BRIEF) — the operator already chose the service on
  // the work order. When the RFQ predates that choice it carries priced
  // options with none selected; resolve the WO's service to its matching
  // priced option so this surface treats the question as answered (the
  // pricing block renders that line, the guided card moves to "generate
  // quote") instead of asking again.
  //
  // Match by the catalog SERVICE ID first (`s.service.id === wo.service.id`),
  // NOT by name. The estimate line's `name` is copied from the provider's
  // ComboProduct service, which can read differently from the catalog
  // service the WO points at (e.g. the WO service is "Install Only" while
  // the priced line's combo name is "Installation of Mulch"). A name match
  // then finds nothing and the surface silently prices the lone line under
  // the wrong name — the SERVICE TYPE / quote-line mismatch. The id is the
  // stable identity and matches regardless of the copied label. Name is a
  // fallback ONLY when exactly one line carries it (the catalog also has
  // duplicate ids per name, so an ambiguous name stays a real choice).
  const woServiceId = wo?.service?.id ?? null;
  const woServiceName = wo?.service?.name ?? null;
  const woMatchedById = woServiceId
    ? estimateServices.filter((s: any) => (s?.service?.id ?? null) === woServiceId)
    : [];
  const woMatchedByName = woServiceName
    ? estimateServices.filter((s: any) => (s?.service?.name ?? s?.name) === woServiceName)
    : [];
  const woResolvedService = !estimate?.selectedService
    ? woMatchedById.length === 1
      ? woMatchedById[0]
      : woMatchedByName.length === 1
      ? woMatchedByName[0]
      : null
    : null;

  const pricedService =
    estimate?.selectedService ??
    woResolvedService ??
    (estimateServices.length === 1 ? estimateServices[0] : null);
  const ambiguousPricing = !pricedService && estimateServices.length > 1;

  /**
   * W-m (RC5) — choosing between service options no longer means leaving.
   * The RFQ prices several options when the estimate engine returns more
   * than one (installation vs delivery-only, say). Picking one is the
   * same `updateQuoteRequest` write the RFQ page makes via
   * `selectServices`; there is no reason the operator has to go there to
   * do it. Refetches the RFQ afterwards because the write mints a new
   * estimate version.
   */
  const [selectingService, setSelectingService] = useState<string | null>(null);
  const chooseService = async (serviceId: string) => {
    if (!rfq?.id) return;
    setSelectingService(serviceId);
    const res = await updateQuoteRequest({
      data: { id: rfq.id },
      update: { selectServices: [serviceId] },
    });
    setSelectingService(null);
    if (res?.id) {
      toast.add("Service selected. Pricing is set for this work order.", "var(--theme-ui-colors-success)");
      loadRfq();
    } else {
      toast.add(res?.message || "Could not select that service.", "var(--theme-ui-colors-danger)");
    }
  };

  const lineItems = useMemo(() => {
    if (!pricedService) return [];
    const rows: { label: string; amount: number }[] = [];
    const qty = Number(pricedService.quantity || 0);
    const unitPrice = parseFloat(pricedService.price || 0) || 0;
    rows.push({
      label: `${pricedService.product?.name || pricedService.name || "Service"} · ${qty} ${
        pricedService.product?.unit || ""
      }`.trim(),
      amount: qty * unitPrice,
    });
    for (const addon of pricedService.addons || []) {
      if (!(addon.quantity > 0)) continue;
      rows.push({
        label: addon.name || "Add-on",
        amount: Number(addon.quantity) * (parseFloat(addon.price || 0) || 0),
      });
    }
    return rows;
  }, [pricedService]);

  const estimateTotal = lineItems.reduce((sum, r) => sum + r.amount, 0);

  /**
   * W-i + W-q (RC5, mockup 27) — the WORK ORDER specifies what it wants;
   * the RFQ prices it. So the pricing block has to state whether the
   * priced line actually answers this work order, on BOTH axes:
   *   PRODUCT  — the material (Playground Mulch vs Compost).
   *   SERVICE  — the service type (delivery vs installation).
   * A price for the right material under the wrong service is exactly as
   * wrong as the reverse, and far easier to miss.
   *
   * Unknown is NOT a mismatch: older WOs carry neither, and the WO's
   * service is legitimately blank when the operator chose to price every
   * option. Only claim a mismatch when both sides are known and differ.
   */
  const serviceMatch = (() => {
    if (!pricedService) return null;
    // A cancelled WO has nothing to re-price - the case is dead and the
    // quote is retired. Suppress the mismatch banner + "Re-price RFQ"
    // button entirely (this is the single source the pricing block reads).
    if (wo?.status === "rejected") return null;

    const woProductId = wo?.product?.id ?? null;
    const pricedProductId = pricedService?.product?.id ?? null;
    const productKnown = !!woProductId && !!pricedProductId;
    const productMismatch = productKnown && woProductId !== pricedProductId;

    // The priced row's service name is the reliable comparison: the
    // trunk catalog carries duplicate service ids for the same service
    // name, so an id comparison alone reports false mismatches.
    const woServiceName = wo?.service?.name ?? null;
    const pricedServiceName = pricedService?.service?.name ?? pricedService?.name ?? null;
    const serviceKnown = !!woServiceName && !!pricedServiceName;
    const serviceMismatch = serviceKnown && woServiceName !== pricedServiceName;

    if (productMismatch) {
      return {
        state: "mismatch" as const,
        what: "product" as const,
        woName: wo?.product?.name ?? "this work order's product",
        pricedName: pricedService?.product?.name ?? "another product",
      };
    }
    if (serviceMismatch) {
      return {
        state: "mismatch" as const,
        what: "service" as const,
        woName: woServiceName as string,
        pricedName: pricedServiceName as string,
      };
    }
    if (!productKnown && !serviceKnown) {
      return { state: "unknown" as const, what: "product" as const, woName: null, pricedName: null };
    }
    return {
      state: "match" as const,
      what: serviceKnown ? ("service" as const) : ("product" as const),
      woName: null,
      pricedName: serviceKnown
        ? (pricedServiceName as string)
        : (pricedService?.product?.name ?? null),
    };
  })();
  const nteAmount =
    wo?.nteAmount != null && wo?.nteAmount !== "" ? Number(wo.nteAmount) : null;
  const nteDelta = nteAmount != null && pricedService ? estimateTotal - nteAmount : null;

  /* ── stage + attached records ─────────────────────────────────── */

  const stageOrder = typeof wo?.stageOrder === "number" ? wo.stageOrder : 0;
  const stageClosed = stageOrder >= 9;
  // mockup 34 (Q1) — the true "case done" terminal, stamped by the
  // backend alongside the bars: every money leg that exists has settled
  // (Invoice = Paid AND, when a PO exists, PO Billed/Closed; a
  // self-performed case closes on Invoice = Paid alone). Distinct from
  // `stageClosed` (a cancelled/rejected WO); a live case can be
  // caseClosed=false the whole way and only flip true when the last leg
  // settles. Absent on an older payload -> false (Case open).
  const caseClosed = !!wo?.caseClosed;

  // A16 — the per-record progress bars, stamped by the backend
  // (deriveWorkOrderStage.records). Empty on an older payload; the strip
  // renders nothing rather than breaking.
  const stageRecords: any[] = Array.isArray(wo?.stageRecords) ? wo.stageRecords : [];

  // A10 — the LEFT column is tabbed (Maps / Pricing / Quote / Project /
  // PO). The default tab lands where the operator's next action is. Once
  // a PROJECT begins it stays the default until the project is Closed
  // (all its steps complete): fulfillment is the live work, and the
  // operator should not be bumped to PO/invoice mid-project (Session 65
  // item 1 / root cause A — reload and post-accept refresh were landing
  // on PO). Only after the project completes does the default advance to
  // the furthest-along money leg (invoice, then po). Before a project
  // exists, follow the case left-to-right (quote / pricing / maps). A
  // `?tab=` param (deep link, or the operator's click) overrides.
  // Resolved off the bars so it tracks the same live records the strip
  // shows.
  const rfqBar = stageRecords.find((b) => b.key === "rfq") ?? null;
  const quoteBar = stageRecords.find((b) => b.key === "quote") ?? null;
  const projectBar = stageRecords.find((b) => b.key === "project") ?? null;
  const invoiceBar = stageRecords.find((b) => b.key === "invoice") ?? null;
  const poBar = stageRecords.find((b) => b.key === "po") ?? null;
  const started = (b: any) => !!b && b.state !== "not_started";
  // The project owns the default tab for as long as the shared next-
  // action resolver says the project is the live front. That is the same
  // signal the WhatsNext card uses (`track_project` marked primary),
  // which now holds through the Complete->Invoice Sent "Add invoice"
  // window (item 5) as well as ordinary in-progress milestones — so the
  // default tab and the WhatsNext step can never disagree about whether
  // the operator is still working the project. Resolved once here off the
  // loaded chain; `nextAction` below re-resolves for the card.
  const woNextForDefault = resolveWoNextAction(wo, {
    intakeStyle: wo?.servicePlan?.intakeStyle ?? null,
  });
  const projectOpen =
    woNextForDefault.kind === "track_project" && woNextForDefault.primary;
  const defaultWoTab = projectOpen
    ? "project"
    : started(poBar)
    ? "po"
    : started(invoiceBar)
    ? "invoice"
    : started(projectBar)
    ? "project"
    : started(quoteBar)
    ? "quote"
    : started(rfqBar)
    ? "pricing"
    : "maps";
  const VALID_TABS = ["maps", "pricing", "quote", "project", "invoice", "po"];
  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : defaultWoTab;
  const setActiveTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const quotes: any[] = Array.isArray(wo?.convertedRfq?.quotes) ? wo.convertedRfq.quotes : [];
  // The "current" quote is the LIVE one, not merely the highest sequence.
  // Sorting by sequence alone surfaced a SUPERSEDED quote as the primary
  // (its Open link opened the retired version) whenever the retired quote
  // outranked the live one by sequence. Rank by status first: an accepted
  // (1) quote is the current one; else the newest still-active quote
  // (New 0 / Draft 3 / Price Locked 6); only if there is no live quote at
  // all do we fall back to the newest terminal one (Superseded 5 /
  // Denied 2 / Expired 4) so the row still shows something. Within a tier,
  // higher sequence wins.
  const quoteRank = (status: number): number => {
    if (status === 1) return 3; // Accepted - the live, current quote
    if (status === 0 || status === 3 || status === 6) return 2; // still active
    return 1; // terminal (superseded / denied / expired)
  };
  const newestQuote = quotes.length
    ? [...quotes].sort((a: any, b: any) => {
        const r = quoteRank(Number(b?.status)) - quoteRank(Number(a?.status));
        if (r !== 0) return r;
        return (b?.sequence ?? 0) - (a?.sequence ?? 0);
      })[0]
    : null;

  // W-k — load the newest quote in full whenever the WO points at one.
  const newestQuoteId = newestQuote?.id ?? null;
  const loadQuote = useCallback(async () => {
    if (!newestQuoteId) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    const res = await getQuote({ data: { id: newestQuoteId } });
    setQuote(res?.id ? res : null);
    setQuoteLoading(false);
  }, [newestQuoteId]);

  // Session 65 (on-demand fix) - load a specific quote by id and seed it
  // as the active `quote` immediately, independent of the WO reload.
  // After generating a quote, `load()` refetches the WO, but under Galera
  // replica lag the fresh WO read can momentarily miss the just-created
  // quote in `convertedRfq.quotes`, so `newestQuoteId` stays stale and
  // the delivery panel (gated on `quote?.id`) doesn't appear until a
  // manual refresh. Seeding straight from the generate response's id
  // makes the panel show on demand, no refresh.
  const loadQuoteById = async (id: string) => {
    if (!id) return;
    setQuoteLoading(true);
    const res = await getQuote({ data: { id } });
    setQuote(res?.id ? res : null);
    setQuoteLoading(false);
  };

  // Session 65 (WhatsNext staleness fix) - when the quote is marked posted
  // to Corrigo from the embedded delivery panel, the WhatsNext card (which
  // derives from `wo`) must flip from "post to Corrigo" to "accept on
  // behalf" WITHOUT a manual refresh. The server stamps
  // `corrigoQuoteSubmittedAt` from an audit row, but an immediate WO
  // reload can miss it under Galera replica lag. So optimistically stamp
  // that field on the newest quote in local `wo` state - both
  // resolveWoNextAction calls re-derive instantly - then also reload to
  // reconcile with the server truth.
  const onQuotePosted = () => {
    setWo((prev: any) => {
      if (!prev?.convertedRfq?.quotes?.length) return prev;
      const nowIso = new Date().toISOString();
      const stampedQuotes = prev.convertedRfq.quotes.map((q: any) =>
        q?.id === newestQuoteId
          ? { ...q, corrigoQuoteSubmittedAt: q.corrigoQuoteSubmittedAt ?? nowIso }
          : q,
      );
      return {
        ...prev,
        convertedRfq: { ...prev.convertedRfq, quotes: stampedQuotes },
      };
    });
    load();
  };

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  // Quote line items are `quote.items`, nested one level (the trunk
  // Overview tab flattens them the same way).
  const quoteItems: any[] = Array.isArray(quote?.items) ? quote.items.flat() : [];
  const quoteTotal =
    quote?.total != null
      ? Number(quote.total)
      : quoteItems.reduce(
          (sum: number, it: any) => sum + Number(it?.price ?? 0) * Number(it?.quantity ?? 0),
          0,
        );
  const quoteNteDelta =
    nteAmount != null && quoteItems.length > 0 ? quoteTotal - nteAmount : null;
  const sendPrefillEmail =
    wo?.convertedRfq?.siteContactEmail || newestQuote?.siteContactEmail || "";
  const attachedProject =
    wo?.convertedRfq?.project ?? newestQuote?.project ?? null;
  const attachedInvoice = newestQuote?.invoice ?? null;
  // Same status wording as every other invoice pill (InvoiceStatusBadge):
  // an early-payment email reads "Sent - Early", work done reads
  // "Created - Ready to bill".
  const attachedInvoiceDisplay = attachedInvoice
    ? getInvoiceStatusDisplay(attachedInvoice, {
        isStaff: true,
        projectStatusName: attachedProject?.status?.name ?? null,
      })
    : null;
  const attachedInvoiceLabel = attachedInvoiceDisplay
    ? attachedInvoiceDisplay.tag
      ? attachedInvoiceDisplay.label + " \u00b7 " + attachedInvoiceDisplay.tag.text
      : attachedInvoiceDisplay.label
    : "Created";
  // "Already sent" for the purpose of hiding the Send invoice button. An
  // invoice is past sending if it carries a sentDate, or its status is any
  // post-send state: Sent(1), Paid(2), or Partial Paid(3). (Created(0) and
  // Voided(4) are the only states where sending still makes sense - and we
  // don't offer send on a voided invoice either.) statusName alone missed
  // Partial Paid, which is why the button kept showing after a payment.
  const invoiceAlreadySent =
    !!attachedInvoice?.sentDate ||
    [1, 2, 3].includes(Number(attachedInvoice?.status));
  // Session 65 item 7a: seed the in-place scheduled-date input from the
  // project's stored scheduleDate whenever the attached project changes,
  // so the editor opens pre-filled with any existing date.
  const attachedProjectScheduleDate = (attachedProject as any)?.scheduleDate ?? null;
  useEffect(() => {
    // SelectDate emits and expects a Unix timestamp (see the trunk project
    // edit, which seeds `moment.utc(scheduleDate).unix()`); match it so
    // the value round-trips and the save sends a real date string.
    setScheduleDateInput(
      attachedProjectScheduleDate
        ? moment.utc(attachedProjectScheduleDate).unix()
        : null,
    );
  }, [attachedProjectScheduleDate]);
  // A26 — the WO's purchase order(s), resolved server-side by
  // quote/project id and hung on the payload as `attachedPurchaseOrders`
  // (no entity-graph path from the WO chain to a PO; see
  // WorkOrderService.attachPurchaseOrders). Usually one, occasionally a
  // few (one per quote), so the rail renders a row each.
  const attachedPurchaseOrders: any[] = Array.isArray(wo?.attachedPurchaseOrders)
    ? wo.attachedPurchaseOrders
    : [];

  // Session 59 (Part B) — lazy full-record loads for the terms editor.
  const attachedInvoiceId = attachedInvoice?.id ?? null;
  const loadInvoiceFull = useCallback(async () => {
    if (!attachedInvoiceId) {
      setInvoiceFull(null);
      return;
    }
    const res = await getInvoice({ data: { id: attachedInvoiceId } });
    setInvoiceFull(res?.id ? res : null);
  }, [attachedInvoiceId]);
  useEffect(() => {
    if (activeTab === "invoice") loadInvoiceFull();
  }, [activeTab, loadInvoiceFull]);

  // Invoice-tab: load the project's photos (for the photo include picker)
  // and the attached map's snapshot count (to gate the snapshot picker).
  // Both are company-only reads and only needed while the Invoice tab is
  // open with an invoice present. `_getProjectPhotos` is also the
  // picker's onChanged refetch so the count + inline PDF stay in sync.
  const attachedProjectIdForPhotos = attachedProject?.id ?? null;
  const _getProjectPhotos = useCallback(async () => {
    if (!attachedProjectIdForPhotos) {
      setProjectPhotos([]);
      return;
    }
    const res = await getProjectPhotos({ data: { id: attachedProjectIdForPhotos } });
    setProjectPhotos(Array.isArray(res) ? res : []);
  }, [attachedProjectIdForPhotos]);
  useEffect(() => {
    if (activeTab === "invoice") _getProjectPhotos();
  }, [activeTab, _getProjectPhotos]);

  // Snapshot count for the attached invoice's map version.
  const invoiceMulchMapId = (invoiceFull?.mulchMapId ?? attachedInvoice?.mulchMapId) || null;
  useEffect(() => {
    if (activeTab !== "invoice" || !invoiceMulchMapId) {
      setInvoiceSnapshotCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const snapshots = await getMapSnapshots(invoiceMulchMapId);
      if (cancelled) return;
      setInvoiceSnapshotCount(Array.isArray(snapshots) ? snapshots.length : 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, invoiceMulchMapId]);

  // Load quote history when the Project OR Quote tab opens (declared here,
  // below `activeTab`, so it does not reference the var before its
  // declaration). The Quote tab shows a compact version list so operators
  // can navigate versions where they look for them; the Project tab keeps
  // the full audit. Both read the same project-scoped history, so it only
  // populates once a project exists (pre-project there is one quote and
  // nothing to list).
  useEffect(() => {
    if (
      (activeTab === "project" || activeTab === "quote") &&
      attachedProjectId &&
      quoteHistory === null
    ) {
      loadQuoteHistory();
    }
  }, [activeTab, attachedProjectId, quoteHistory, loadQuoteHistory]);

  const loadPoFull = useCallback(async (poId: string) => {
    if (!poId) return;
    const res = await getPurchaseOrder({ data: { id: poId } });
    if (res?.id) setPoFull((m) => ({ ...m, [poId]: res }));
  }, []);
  useEffect(() => {
    if (activeTab !== "po") return;
    for (const po of attachedPurchaseOrders) {
      if (po?.id && !poFull[po.id]) loadPoFull(po.id);
    }
    // eslint-disable-next-line
  }, [activeTab, attachedPurchaseOrders, loadPoFull]);

  // ─── Record PDF downloads (Session 58) ───
  // Surface the SAME PDF the quote/invoice/PO detail pages generate on
  // their own "download" link, right here on the work order, so staff can
  // pull a record's PDF without opening each record ("work in place").
  // Each reuses the record's existing `/…/:id/pdf` endpoint (the exact
  // path A14 already used for the Corrigo panels) — one PDF path, not a
  // second. The endpoints return `{ result: <url> }`; we anchor-download
  // that url, identical to Quote/Invoice Overview's own handlers. RFQ and
  // Project have no PDF, so no download is offered on those rail rows.
  const [pdfDownloadingKey, setPdfDownloadingKey] = useState<string | null>(null);
  const downloadRecordPdf = async (
    key: string,
    fetchPdf: () => Promise<any>,
    filename: string,
  ) => {
    setPdfDownloadingKey(key);
    try {
      const res = await fetchPdf();
      if (!res?.result) {
        toast.add("Could not generate the PDF.", "var(--theme-ui-colors-danger)");
        return;
      }
      const link = document.createElement("a");
      link.href = res.result;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.add("Could not generate the PDF.", "var(--theme-ui-colors-danger)");
    }
    setPdfDownloadingKey(null);
  };
  const downloadQuotePdf = (q: any) => {
    // Mirrors Quote Overview's filename convention (GMQ_<seq>[-ver]).
    const versionSuffix =
      q?.majorVersion > 1 || q?.minorVersion > 0
        ? q.minorVersion > 0
          ? `-${q.majorVersion}.${q.minorVersion}`
          : `-${q.majorVersion}`
        : "";
    return downloadRecordPdf(
      `quote:${q.id}`,
      () => getQuotePDF({ data: { id: q.id } }),
      `GMQ_${String(q.sequence ?? 0).padStart(5, "0")}${versionSuffix}_quote.pdf`,
    );
  };
  const downloadInvoicePdf = (inv: any) =>
    downloadRecordPdf(
      `invoice:${inv.id}`,
      () => getInvoicePDF({ data: { id: inv.id } }),
      `GMI_${String(inv.sequence ?? 0).padStart(5, "0")}_invoice.pdf`,
    );
  const downloadPoPdf = (po: any) =>
    downloadRecordPdf(
      `po:${po.id}`,
      () => getPoPDF({ data: { id: po.id } }),
      `GMO_${String(po.sequence ?? 0).padStart(5, "0")}_purchase_order.pdf`,
    );

  // PO BLOCKER (Session 57, mockup 33 follow-on). The PO is a PARALLEL
  // fulfillment track hanging off the project, not a rung on the case
  // spine (RFQ → quote → accept → project → invoice → submit) — so it does
  // not belong in the single "what's next" ladder. But a PO that is stuck
  // CAN hold the project up (you cannot schedule the crew until the
  // provider has committed), so the case card surfaces it as a secondary
  // line rather than the primary step. It counts as blocking only while
  // the project is live but not yet Complete AND a PO is New (not sent)
  // or Sent (awaiting the provider). Accepted+ POs, or a Complete
  // project, are not blocking the next move.
  const projectStepNum =
    attachedProject?.status?.stepNumber != null
      ? Number(attachedProject.status.stepNumber)
      : null;
  const projectLiveNotComplete =
    projectStepNum != null && projectStepNum > 0 && projectStepNum < 5;
  const blockingPo =
    projectLiveNotComplete
      ? attachedPurchaseOrders.find((po: any) => {
          const s = Number(po?.status ?? 0);
          return s === 0 || s === 1; // New (not sent) or Sent (awaiting provider)
        }) ?? null
      : null;
  const poBlocker = blockingPo
    ? {
        text:
          Number(blockingPo.status ?? 0) === 0
            ? `PO ${
                blockingPo.sequence != null ? `GMO-${pad(blockingPo.sequence)}` : ""
              } hasn't been sent to the provider yet.`.replace("  ", " ")
            : `PO ${
                blockingPo.sequence != null ? `GMO-${pad(blockingPo.sequence)}` : ""
              } is sent — waiting on the provider to accept.`.replace("  ", " "),
        onClick: () => setActiveTab("po"),
      }
    : null;

  // Generate the quote from the WO, using the same trunk call the RFQ
  // surface makes. Stays on this page: the quote attaches back to the
  // WO and the strip advances (RC1). Session 65 item 2: after the quote
  // is minted, switch to the Quote tab so the newly activated record is
  // both selected and shown (the reload happens via load()).
  const generateQuote = async () => {
    if (!estimate?.id || !pricedService?.id) return;
    setGeneratingQuote(true);
    const res = await generateQuoteFromEstimate({
      data: { id: estimate.id },
      service: { selectServices: [pricedService.id] },
    });
    setGeneratingQuote(false);
    if (res?.id) {
      toast.add("Quote generated and attached to this work order.", "var(--theme-ui-colors-success)");
      // Seed the quote directly from the generate response so the Quote
      // tab's delivery panel appears on demand (no refresh), then reload
      // the WO for the rest of the case file.
      loadQuoteById(res.id);
      load();
      setActiveTab("quote");
    } else {
      toast.add(res?.message || "Could not generate the quote.", "var(--theme-ui-colors-danger)");
    }
  };

  /* ── guided next action (W-c) ─────────────────────────────────── */

  type NextAction = {
    title: string;
    body: string;
    label: string;
    onClick: () => void;
    submitting?: boolean;
  };

  const nextAction: NextAction | null = (() => {
    /**
     * The LADDER is resolved by the shared `workOrderNextAction` (the
     * same one the plan work queue uses), so the case file and the row
     * cannot disagree about what a work order needs next — they did,
     * and the row was wrong. What stays local is HOW each step is
     * performed: this surface acts in place wherever it can (RC5), so
     * "generate quote" runs here rather than navigating, and the send /
     * post step opens the modal instead of routing to the quote. The
     * resolver supplies kind + label + hint; the map below supplies the
     * handler.
     */
    const next = resolveWoNextAction(wo, {
      intakeStyle: wo?.servicePlan?.intakeStyle ?? null,
    });
    if (next.kind === "none") return null;

    const titleFor = (label: string) =>
      next.primary ? `Next: ${label.toLowerCase()}` : label;

    switch (next.kind) {
      case "match":
        return {
          title: "Next: match this work order to a site",
          body: next.hint ?? "",
          label: "Match site",
          onClick: () => setShowMatch(true),
        };

      case "generate_rfq":
        if (rfqCandidates.length) {
          const c = rfqCandidates[0];
          const seq = c?.sequence != null ? `#${String(c.sequence).padStart(5, "0")}` : "";
          return {
            title: "Next: attach the existing RFQ",
            body:
              rfqCandidates.length === 1
                ? `RFQ ${seq}${c?.quote ? " and its quote" : ""}${c?.project ? " and project" : ""} already exist for this client at this site, but aren't attached to this work order. Attach it instead of generating a duplicate.`
                : `${rfqCandidates.length} RFQs already exist for this client at this site. Attach the one for this work order instead of generating a duplicate.`,
            label: "Attach existing RFQ",
            onClick: () => setShowAttachRfq(true),
          };
        }
        return {
          title: "Next: generate the RFQ",
          body: "Generate an RFQ for this work order to price it. The RFQ attaches back here and this work order stays the record of the project.",
          label: "Generate RFQ",
          onClick: openGenerateRfq,
          submitting: generatingRfq,
        };

      case "generate_quote": {
        // The resolver says the case is ready to be quoted. Whether that
        // can happen HERE depends on the pricing being unambiguous.
        if (pricedService) {
          // RE-QUOTE vs first quote. When the newest quote is terminal
          // (Superseded 5 / Denied 2 / Expired 4) and there is no live
          // one, the prior quote died — most often because the RFQ was
          // re-priced, which supersedes the quote but does NOT mint a
          // replacement. The old copy ("Pricing is set. Generate the
          // quote to send to the client") read as if nothing had happened
          // yet, which is what looked wrong on a superseded case. Use the
          // resolver's supersede-aware label + hint ("Re-quote" / "This
          // quote is no longer live") so the card states the real step.
          const newestStatus = Number(newestQuote?.status);
          const isRequote = [2, 4, 5].includes(newestStatus);
          if (isRequote) {
            return {
              title: "Next: re-quote",
              body:
                next.hint ??
                "The previous quote was superseded. Generate a fresh one for the corrected pricing.",
              label: next.label || "Re-quote",
              onClick: generateQuote,
              submitting: generatingQuote,
            };
          }
          return {
            title: "Next: generate the quote",
            body:
              nteDelta != null && nteDelta > 0
                ? "Pricing is over the NTE ceiling. Adjust it on the RFQ, or generate the quote anyway if the overage is approved."
                : "Pricing is set. Generate the quote to send to the client.",
            label: "Generate quote",
            onClick: generateQuote,
            submitting: generatingQuote,
          };
        }
        return {
          title: "Next: price the RFQ",
          body: ambiguousPricing
            ? "The RFQ priced more than one service option. Choose the right one in the pricing block above to set this work order's price."
            : "The RFQ has no priced service yet. Select services and pricing on the RFQ.",
          label: ambiguousPricing ? "Choose a service option" : "Open RFQ",
          onClick: () => {
            if (ambiguousPricing) return; // the picker is on this page
            if (rfqId) navigate(`/quote-requests/${rfqId}`);
          },
        };
      }

      case "post_to_corrigo":
      case "send_quote":
        // Both open the same delivery modal (the shared QuoteDeliveryPanel
        // behind it), which offers Email | CorrigoPro. `post_to_corrigo`
        // is a work_order_fed step, so open it straight on the Corrigo
        // branch; `send_quote` is quote_fed, so let it default to Email.
        // Session 65 item 3: the morph to "Accept on behalf of client"
        // happens at the resolver — once the quote is posted to Corrigo
        // (`corrigoQuoteSubmittedAt`) the resolver returns
        // `record_acceptance` instead, handled below.
        return {
          title: titleFor(next.label),
          body: next.hint ?? "",
          label: next.label,
          onClick: () => {
            setSendQuoteBranch(next.kind === "post_to_corrigo" ? "corrigo" : "email");
            setShowSendQuote(true);
          },
        };

      case "record_acceptance":
        // The morphed step: the quote is posted to Corrigo and the FM has
        // (or is about to) approve. "Accept on behalf of client" is done
        // in place through the same delivery modal, opened on the Corrigo
        // branch — its embedded panel shows the accept-on-behalf form once
        // the submit step is checked — rather than navigating away (RC5).
        return {
          title: next.label,
          body: next.hint ?? "",
          label: next.label,
          onClick: () => {
            setSendQuoteBranch("corrigo");
            setShowSendQuote(true);
          },
        };

      case "awaiting_client":
        return {
          title: "Awaiting the client",
          body: next.hint ?? "",
          label: "View quote",
          onClick: () => navigate(`/quotes/${newestQuote?.id}`),
        };

      case "schedule_project":
        return {
          title: "Accepted",
          body: next.hint ?? "",
          label: attachedProject?.id ? "View project" : "Schedule work",
          onClick: () =>
            attachedProject?.id
              ? navigate(`/projects/${attachedProject.id}`)
              : navigate(`/quotes/${newestQuote?.id}`),
        };

      case "track_project": {
        // Two shapes come through here. The GATE case (resolver marks it
        // primary) means the project is live and its next milestone is
        // the case's next step — up to and including "Add invoice" (item
        // 5, the Complete->Invoice Sent transition). Session 65 items 2 +
        // 4: this WhatsNext button must DRIVE that step, not just reveal
        // the tab. It fires the SAME `advanceProject` the in-tab stepper
        // uses on the live step (`projectNextSteps[0]`) so the two
        // controls can never disagree, then shows the project tab so the
        // result is in view. If the live step is a REDIRECT (a trunk
        // action that lives on the project page), `advanceProject`
        // already routes there; if no step has loaded yet, fall back to
        // simply activating the tab. The passive case (label "View
        // project") is post-invoice tracking — a look — so it opens the
        // project page as before.
        if (next.primary) {
          const liveStep =
            Array.isArray(projectNextSteps) && projectNextSteps.length > 0
              ? projectNextSteps[0]
              : null;
          // Item 5: when the resolver's project step is the invoice step
          // and the invoice ALREADY exists (raised at project creation on
          // a maintenance plan), this is "send", not "add". Relabel to
          // "Send invoice" and jump to the invoice tab (where the send
          // button + include pickers live) instead of running the
          // add-invoice redirect / create form.
          const invoiceStepButExists =
            next.label === "Add invoice" && !!attachedInvoice?.id;
          if (invoiceStepButExists) {
            return {
              title: "Next: send the invoice",
              body: next.hint ?? "",
              label: "Send invoice",
              onClick: () => setActiveTab("invoice"),
            };
          }
          return {
            title:
              next.label === "Add invoice"
                ? "Next: add the invoice"
                : "Next: advance the project",
            body: next.hint ?? "",
            label: next.label || "Advance project",
            onClick: () => {
              setActiveTab("project");
              if (liveStep) advanceProject(liveStep);
            },
            submitting: projectAdvancing,
          };
        }
        return {
          title: "In production",
          body: next.hint ?? "",
          label: "View project",
          onClick: () => navigate(`/projects/${attachedProject?.id}`),
        };
      }

      case "submit_invoice_corrigo":
      case "send_invoice":
      case "view_invoice":
        // Session 65 item 5: the invoice already exists by this point, so
        // the next step is to SEND it (or submit to Corrigo) - and that is
        // done in place on the WO invoice tab, which carries the Send
        // invoice button, the include-on-invoice pickers (site map +
        // photos), and the Corrigo submit panel. Land on that tab rather
        // than navigating away to the standalone invoice page.
        return {
          title: next.kind === "submit_invoice_corrigo" ? titleFor(next.label) : "Invoiced",
          body: next.hint ?? "",
          label: next.label,
          onClick: () => setActiveTab("invoice"),
        };

      default:
        return null;
    }
  })();

  /* ── history (W-f) ────────────────────────────────────────────── */

  const historyEvents = useMemo(() => {
    const rows: {
      at: any;
      title: string;
      sub?: string | null;
      tone: string;
      actor?: string | null;
    }[] = [];

    /**
     * RECORDED events first — these carry an author and a timestamp of
     * the action itself, which the chain-derived rows below cannot.
     * Both are shown together: work orders created before the activity
     * log existed have no events at all, and for those the derived
     * chain is the only history there is. A recorded event and a
     * derived row can describe the same moment (an RFQ generation, say)
     * and that is acceptable — the derived one carries no actor, so it
     * reads as the record appearing rather than a duplicate claim about
     * who did it.
     */
    for (const e of woEvents) {
      rows.push({
        at: e?.createdAt ?? null,
        title: e?.message || e?.type || "Activity",
        sub: null,
        tone: eventTone(e?.type),
        actor: eventActor(e),
      });
    }

    if (attachedInvoice?.id) {
      rows.push({
        at: attachedInvoice.createdAt ?? null,
        title: "Invoice raised",
        sub: attachedInvoice.sequence != null ? `INV-${pad(attachedInvoice.sequence)}` : null,
        tone: mnt.green,
      });
    }
    if (attachedProject?.id) {
      rows.push({
        at: attachedProject.createdAt ?? null,
        title: "Project created",
        sub: attachedProject.name || null,
        tone: mnt.green,
      });
    }
    for (const q of quotes) {
      rows.push({
        at: q?.createdAt ?? null,
        title: q?.sequence != null ? `Quote GMP-${pad(q.sequence)} generated` : "Quote generated",
        sub: q?.status === 1 ? "Accepted" : null,
        tone: mnt.link,
      });
    }
    if (wo?.convertedRfq?.id) {
      rows.push({
        at: wo.convertedRfq.createdAt ?? null,
        title:
          wo.convertedRfq.sequence != null
            ? `RFQ GMR-${pad(wo.convertedRfq.sequence)} generated for this work order`
            : "RFQ generated for this work order",
        sub: null,
        tone: mnt.link,
      });
    }
    if (wo?.receivedAt) {
      rows.push({
        at: wo.receivedAt,
        title: "Work order received",
        sub: wo.sourceType ? sourceLabel(wo.sourceType) : null,
        tone: mnt.green,
      });
    }
    return rows.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
    // eslint-disable-next-line
  }, [wo, attachedProject, attachedInvoice, woEvents]);

  if (loading) {
    return (
      <>
        <Header></Header>
        <Page title="Work Order">
          <Box sx={{ textAlign: "center", paddingY: "60px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  if (notFound || !wo) {
    return (
      <>
        <Header></Header>
        <Page title="Work Order">
          <Box sx={{ padding: "40px", textAlign: "center" }}>
            <Subtext fontSize={"13px"} fontFamily={"Poppins"}>
              This work order could not be found.
            </Subtext>
            <Box sx={{ marginTop: "16px" }}>
              <BasicButton
                type="button"
                outline
                scheme="secondary"
                onClick={() => navigate("/maintenance/work-orders")}
              >
                Back to queue
              </BasicButton>
            </Box>
          </Box>
        </Page>
      </>
    );
  }

  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || null;
  const planName = wo.servicePlan?.name || null;
  const batchName = wo.group?.name || null;
  const hasBatches = Array.isArray(planGroups) && planGroups.length > 0;

  // A12 — the header identity, resolved in the one shared place. The
  // number leads ("Work Order 374295-..."); when the WO has only a name,
  // the header reads that instead. Any descriptor line (the secondary)
  // rides in the subtitle so the title stays a single clean handle.
  const identity = workOrderDisplayIdentity(wo);

  const subtitleParts = [
    identity.secondary,
    siteName,
    [address?.line1, city].filter(Boolean).join(", ") || null,
    planName,
    batchName,
  ].filter(Boolean);

  return (
    <>
      <Header></Header>
      <Page
        title={`Work Order ${identity.primary}`.trim()}
        subtitle={subtitleParts.join(" · ")}
        titleAction={
          <Flex sx={{ gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <BasicButton
              type="button"
              outline
              scheme="secondary"
              onClick={() => navigate("/maintenance/work-orders")}
            >
              Back to queue
            </BasicButton>
            <WoManageMenu
              onDuplicate={() => submitDuplicate()}
              onCancel={wo.status !== "rejected" ? () => setShowCancelConfirm(true) : null}
              onDelete={quotes.length === 0 ? () => setShowDeleteConfirm(true) : null}
            />
          </Flex>
        }
      >
        {/* ── CLIENT CANCELLATION REQUEST (Session 80) ─────────────────
            Above the stage ladder, because it decides whether the rest
            of the ladder should happen at all. Amber: this is a pending
            DECISION, not an error. Two actions and no third — Cancel
            runs the existing cascade (which clears this request as part
            of its transaction), Dismiss declines it and the work
            continues. There is no "approve" button because approving IS
            the cancel. ── */}
        {wo?.cancellationRequestedAt && wo?.status !== "rejected" && (
          <Box
            sx={{
              ...mntCard,
              padding: "14px 18px",
              marginBottom: "16px",
              background: mnt.amberPill,
              border: "1px solid " + mnt.amberStroke,
            }}
          >
            <Flex
              sx={{
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "14px",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: mnt.ink,
                  }}
                >
                  The client asked us to cancel this work order
                </Box>
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: mnt.amberDeep,
                    marginTop: "4px",
                  }}
                >
                  {[
                    [
                      wo?.cancellationRequestedBy?.firstName,
                      wo?.cancellationRequestedBy?.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ") || "A client user",
                    relTime(wo.cancellationRequestedAt),
                  ]
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </Box>
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "12.5px",
                    color: mnt.ink,
                    marginTop: "8px",
                  }}
                >
                  {cancellationReasonText(
                    wo?.cancellationReason,
                    wo?.cancellationReasonCategory,
                  )}
                </Box>
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11.5px",
                    color: mnt.textMuted,
                    marginTop: "8px",
                  }}
                >
                  The client has been told work continues until we confirm. Cancelling
                  runs the full teardown — it cancels any sent purchase order and emails
                  the provider.
                </Box>
              </Box>
              <Flex sx={{ gap: "10px", flexWrap: "wrap", flexShrink: 0 }}>
                <BasicButton
                  type="button"
                  scheme="primary"
                  keepText
                  onClick={() => setShowCancelConfirm(true)}
                >
                  Cancel the work order
                </BasicButton>
                <BasicButton
                  type="button"
                  outline
                  scheme="secondary"
                  keepText
                  $submitting={dismissingRequest}
                  disabled={dismissingRequest}
                  onClick={submitDismissCancellationRequest}
                >
                  Dismiss
                </BasicButton>
              </Flex>
            </Flex>
          </Box>
        )}

        {/* ── Mockup 33 header band: progress bars (left) + promoted
            next-action (right, filling the former white space). On narrow
            screens they stack. The next-action card only takes a column
            when there IS a next step; otherwise the bars span full width
            (a closed/done case shouldn't leave an empty right column). ── */}
        <Grid
          columns={nextAction ? [1, "minmax(0, 1fr) 360px"] : [1]}
          gap={"16px"}
          sx={{ alignItems: "stretch", marginBottom: "20px" }}
        >
          {/* ── W-a / A16 per-record progress bars ── */}
          <RecordBarStrip
            records={stageRecords}
            closed={stageClosed}
            closedLabel={wo.status === "rejected" ? "Cancelled" : wo.stageLabel || "Closed"}
            caseClosed={caseClosed}
            hint={wo.stageHint || null}
            activeTab={activeTab}
            onPickTab={setActiveTab}
            noMargin
          />
          {/* the case's ONE next step, at eye level beside the progress */}
          {nextAction && <NextActionCard action={nextAction} poBlocker={poBlocker} />}
        </Grid>

        <Grid columns={[1, "minmax(0, 1fr) 300px"]} gap={"20px"} sx={{ alignItems: "start" }}>
          {/* ═══ LEFT ═══ */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
            {/* ── W-d compact intake band ── */}
            <SectionCard
              title="Intake"
              action={
                <LinkButton
                  label={editingIntake ? "Cancel" : "Edit"}
                  onClick={() => setEditingIntake(!editingIntake)}
                />
              }
            >
              {editingIntake && editable ? (
                <Box sx={{ padding: "16px 18px 18px" }}>
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={submit}
                  >
                    {({ values, errors, submitCount, setFieldValue, dirty }) => (
                      <Form noValidate autoComplete="off">
                        <Grid gap={3} columns={[1, "2fr 2fr 1fr"]}>
                          <SelectInput
                            name="productId"
                            label="Product"
                            value={values.productId}
                            options={[
                              { value: "", label: "Choose a product..." },
                              ...(products || []).map((p: any) => ({ value: p.id, label: p.name })),
                            ]}
                            onChange={(v: any) => {
                              setFieldValue("productId", v || "");
                              setFieldValue("serviceId", "");
                            }}
                            searchable={(products?.length ?? 0) > 8}
                            clearable={false}
                          />
                          {/* W-q — the SERVICE TYPE this work order is
                              asking for. Set it and the generated RFQ is
                              priced for this service alone; leave it and
                              every option is priced for you to choose
                              from once you see the numbers. */}
                          <SelectInput
                            name="serviceId"
                            label="Service type"
                            value={values.serviceId}
                            options={[
                              {
                                value: "",
                                label: values.productId
                                  ? "Decide later — price both"
                                  : "Choose a product first",
                              },
                              ...(
                                (products || []).find((p: any) => p.id === values.productId)
                                  ?.services ?? []
                              ).map((s: any) => ({ value: s.id, label: s.name })),
                            ]}
                            onChange={(v: any) => setFieldValue("serviceId", v || "")}
                            searchable={false}
                            clearable={false}
                            disabled={!values.productId}
                          />
                          <TextInput
                            type="number"
                            name="quantity"
                            label="Quantity"
                            min={0}
                            value={values.quantity}
                            onChange={(val: any) => setFieldValue("quantity", val)}
                            $errors={errors.quantity && submitCount > 0 ? errors.quantity : null}
                            placeholder="e.g. yards"
                          />
                        </Grid>
                        <Subtext
                          fontSize={"11px"}
                          fontFamily={"Poppins"}
                          margin={0}
                          marginTop={"6px"}
                          color={mnt.textMuted}
                        >
                          Product, service type and quantity price the RFQ. Quantity can be set
                          later, but a quantity greater than zero is required to generate the RFQ.
                        </Subtext>

                        {/* Part 1 (SESSION_56) — the RFQ follows the work
                            order. Pre-quote, saving a service-type or
                            quantity change re-prices the RFQ so the two
                            stay consistent; only after a quote is cut is
                            the price committed and a change no longer
                            re-priced. */}
                        {pricingLocked && !priceCommitted && (
                          <Box
                            sx={{
                              marginTop: "10px",
                              padding: "8px 10px",
                              background: mnt.greenPill,
                              borderRadius: "4px",
                            }}
                          >
                            <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.green }}>
                              This work order has an RFQ. Saving a change to the service type or
                              quantity re-prices the RFQ automatically, so its numbers stay matched
                              to this work order. Provider and price are changed in the pricing
                              block above.
                            </Span>
                          </Box>
                        )}
                        {priceCommitted && (
                          <Box
                            sx={{
                              marginTop: "10px",
                              padding: "8px 10px",
                              background: mnt.amberPill,
                              border: `1px solid ${mnt.amberStroke}`,
                              borderRadius: "4px",
                            }}
                          >
                            <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.amber }}>
                              A quote has been generated, so this price is committed to the client.
                              Changing the product, service type or quantity here updates the work
                              order record only — it does not re-price the quote. Generate a new
                              quote version if the number needs to change too.
                            </Span>
                          </Box>
                        )}

                        <Box sx={{ marginTop: "16px" }}>
                          <TextInput
                            type="textarea"
                            name="problemDescription"
                            label="Notes"
                            rows={3}
                            value={values.problemDescription}
                            onChange={(val: any) => setFieldValue("problemDescription", val)}
                            placeholder="Optional. Anything the client noted, or context for the crew."
                          />
                        </Box>

                        <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                          <TextInput
                            name="externalId"
                            label="Work order number"
                            value={values.externalId}
                            onChange={(val: any) => setFieldValue("externalId", val)}
                            placeholder="e.g. Corrigo WO #"
                          />
                          <TextInput
                            name="name"
                            label="Work order name"
                            value={values.name}
                            onChange={(val: any) => setFieldValue("name", val)}
                            placeholder="e.g. Mulch install, north playground"
                          />
                        </Grid>
                        {/* A12 — identity is the number or the name. On a
                            work-order-fed plan at least one is required;
                            the backend refuses a save that would leave
                            neither. */}
                        <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                          <TextInput
                            name="priority"
                            label="Priority"
                            value={values.priority}
                            onChange={(val: any) => setFieldValue("priority", val)}
                            placeholder="e.g. P21 Routine, Urgent"
                          />
                          {/* KNOWN_ISSUES #12 — the intake stamp. Set by the
                              importer (`email_corrigo`) or on manual
                              creation (`manual_upload`) and previously
                              permanent, so a hand-raised WO that turned out
                              to be a Corrigo job could never be corrected
                              and a mis-set import was stuck. Not clearable:
                              every WO has a source.

                              ITEM 18: a CLIENT-RAISED work order reads
                              `client_portal`, which is NOT in this list and
                              must not be — staff selecting it would assert
                              a provenance that never happened, and the
                              update DTO refuses it anyway. Without the
                              branch below the select would render with no
                              matching option and the first pick would
                              silently rewrite an audit field. So a client
                              request shows its source read-only. Re-sourcing
                              one is a correction the backend does allow, but
                              it is deliberately not a one-click accident. */}
                          {wo?.sourceType === "client_portal" ? (
                            <TextInput
                              name="sourceTypeReadOnly"
                              label="Source"
                              value={sourceLabel(wo.sourceType)}
                              disabled
                              onChange={() => undefined}
                            />
                          ) : (
                          <SelectInput
                            name="sourceType"
                            label="Source"
                            value={values.sourceType}
                            options={[
                              { value: "manual_upload", label: "Manual" },
                              { value: "email_corrigo", label: "CorrigoPro" },
                              { value: "email_generic", label: "Email" },
                              { value: "scheduled", label: "Recurring" },
                            ]}
                            onChange={(v: any) =>
                              setFieldValue("sourceType", v || values.sourceType)
                            }
                            searchable={false}
                            clearable={false}
                          />
                          )}
                        </Grid>

                        <Grid gap={3} columns={[1, 2]} sx={{ marginTop: "16px" }}>
                          <TextInput
                            type="number"
                            name="nteAmount"
                            label="NTE (Not To Exceed) amount"
                            min={0}
                            value={values.nteAmount}
                            onChange={(val: any) => setFieldValue("nteAmount", val)}
                            $errors={errors.nteAmount && submitCount > 0 ? errors.nteAmount : null}
                            placeholder="Optional"
                          />
                          <SelectDate
                            name="receivedAt"
                            label="Received"
                            required
                            value={values.receivedAt}
                            onChange={(val: any) => setFieldValue("receivedAt", val)}
                            $errors={errors.receivedAt && submitCount > 0 ? errors.receivedAt : null}
                          />
                        </Grid>

                        <Subtext
                          fontSize={"11px"}
                          fontFamily={"Poppins"}
                          fontWeight={600}
                          margin={0}
                          marginTop={"18px"}
                          marginBottom={"8px"}
                          color={mnt.textMuted}
                        >
                          Due dates
                        </Subtext>
                        <Grid gap={3} columns={[1, 3]}>
                          <SelectDate
                            name="acceptRejectByAt"
                            label="Accept by"
                            value={values.acceptRejectByAt}
                            onChange={(val: any) => setFieldValue("acceptRejectByAt", val)}
                          />
                          <SelectDate
                            name="onSiteByAt"
                            label="On-site by"
                            value={values.onSiteByAt}
                            onChange={(val: any) => setFieldValue("onSiteByAt", val)}
                          />
                          <SelectDate
                            name="completeByAt"
                            label="Complete by"
                            value={values.completeByAt}
                            onChange={(val: any) => setFieldValue("completeByAt", val)}
                          />
                        </Grid>

                        <Flex sx={{ marginTop: "22px", justifyContent: "flex-end" }}>
                          <BasicButton
                            type="submit"
                            $submitting={saving}
                            $iconHeight="14px"
                            $iconWidth="14px"
                            $loadingColor="#fff"
                            keepText={true}
                            disabled={!dirty}
                          >
                            {!saving && <CheckMarkIcon fill={"#fff"} />}
                            Save changes
                          </BasicButton>
                        </Flex>
                      </Form>
                    )}
                  </Formik>
                </Box>
              ) : (
                <Box sx={{ padding: "16px 18px 18px" }}>
                  <Grid gap={"16px 24px"} columns={[1, 3]}>
                    <Field label="Problem" value={wo.problemDescription || "Not stated"} strong />
                    <Field label="Received" value={fmtDate(wo.receivedAt)} />
                    <Field
                      label="Source"
                      value={wo.sourceType ? sourceLabel(wo.sourceType) : "Not set"}
                    />

                    <Field label="Priority" value={wo.priority || "Not set"} />
                    <Field label="Due on-site" value={fmtDate(wo.onSiteByAt)} />
                    <Field label="Due complete" value={fmtDate(wo.completeByAt)} />

                    {/* Scheduled service date - shown once the project has
                        been scheduled (a scheduleDate is set), so the
                        committed on-site date reads with the rest of the
                        intake details. Hidden until scheduled. */}
                    {attachedProjectScheduleDate && (
                      <Field
                        label="Scheduled date"
                        value={fmtDate(attachedProjectScheduleDate)}
                      />
                    )}

                    <Field
                      label="Product"
                      value={wo.product?.name || "Not set"}
                      badge={productIsOverride ? <OverridePill /> : undefined}
                    />
                    {/* W-q — the service type is what the RFQ gets priced
                        for. "Both options" is a real, valid state, not a
                        missing value, so it says so rather than "Not
                        set". */}
                    <Field
                      label="Service type"
                      value={
                        wo.service?.name ||
                        (wo.product ? "Not chosen — price both" : "Not set")
                      }
                      badge={serviceIsOverride ? <OverridePill /> : undefined}
                    />
                    <Field
                      label="Quantity"
                      value={
                        wo.quantity != null && wo.quantity !== ""
                          ? `${Number(wo.quantity)} ${wo.product?.unit || ""}`.trim()
                          : "Not set"
                      }
                    />
                    {/* Resolved provider (and depot). Shown only once the
                        RFQ has priced and a provider is resolved - a fresh,
                        unpriced WO has none, so this stays hidden rather
                        than carrying a dead "Not set" row. Same value the
                        Pricing tab shows (pricedService.depot.provider), so
                        the two surfaces agree. Provider is a fulfillment
                        outcome, not an intake input, but Jack wants it read-
                        able in the main band with the rest. */}
                    {pricedService?.depot?.provider?.name && (
                      <Field
                        label="Provider"
                        value={
                          pricedService.depot.provider.name +
                          (pricedService.depot?.name
                            ? ` \u00b7 ${pricedService.depot.name}`
                            : "")
                        }
                      />
                    )}
                    <Field
                      label="Work order number"
                      value={wo.externalId || "Not set"}
                    />
                    <Field
                      label="Work order name"
                      value={wo.name || "Not set"}
                    />
                  </Grid>
                </Box>
              )}
            </SectionCard>

            {/* A10 — tabbed working area. Intake stays a persistent band
                above; Maps / Pricing / Quote become tabs so an operator
                working a 125-WO batch sees only the surface the current
                stage needs, without scrolling a long stacked page. The
                default tab follows the case's progress (see activeTab).
                Each tab's content is the SAME card that used to stack
                here, now gated on the active tab. */}
            <WoTabBar
              active={activeTab}
              onPick={setActiveTab}
              records={stageRecords}
              hasMaps={!!woLocationId}
              hasQuote={!!newestQuote?.id}
              hasProject={!!attachedProject?.id}
              hasInvoice={!!attachedInvoice?.id}
              hasPo={attachedPurchaseOrders.length > 0}
            />

            {/* Maps at this site — pull a measured map's total into the
                quantity that prices this work order, instead of guessing
                or re-measuring. */}
            {activeTab === "maps" && woLocationId && (
              <SectionCard
                title="Maps at this site"
                action={<LinkButton label="Measure in calculator" onClick={goToCalculator} />}
              >
                <Box sx={{ padding: "12px 16px 14px" }}>
                  {wo.mulchMap?.id && (
                    <Flex
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        background: mnt.greenPill,
                        borderRadius: "6px",
                        padding: "8px 10px",
                        marginBottom: "12px",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"11px"}
                          fontWeight={600}
                          sx={{ color: mnt.green, display: "block" }}
                        >
                          Priced from this map
                        </Span>
                        <Box
                          sx={{
                            ...mntType.body,
                            marginTop: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {wo.mulchMap.name || "Untitled map"}
                          {wo.mulchMap.version ? ` · v${wo.mulchMap.version}` : ""}
                        </Box>
                      </Box>
                      <LinkButton
                        label={linkingMap === "__detach__" ? "Removing…" : "Remove"}
                        onClick={() => setMapForWo(null, "__detach__")}
                      />
                    </Flex>
                  )}
                  {siteMaps === null ? (
                    <Box sx={{ textAlign: "center", paddingY: "12px" }}>
                      <Spinner size={18} strokeWidth={3} title="loading" />
                    </Box>
                  ) : siteMaps.length === 0 ? (
                    <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                      No saved maps at this site yet. Measure it in the calculator to set the
                      quantity from a real map.
                    </Span>
                  ) : (
                    siteMaps.map((m: any, i: number) => (
                      <Flex
                        key={m.mapGroupId}
                        sx={{
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          paddingY: "9px",
                          borderBottom:
                            i === siteMaps.length - 1 ? "none" : `0.5px solid ${mnt.divider}`,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Flex sx={{ alignItems: "center", gap: "8px", minWidth: 0 }}>
                            <Box
                              sx={{
                                ...mntType.body,
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {m.name || "Untitled map"}
                            </Box>
                            {wo.mulchMap?.mapGroupId === m.mapGroupId && (
                              <Box sx={{ ...mntPill("current"), fontSize: "9px", flexShrink: 0 }}>
                                Linked
                              </Box>
                            )}
                          </Flex>
                          <Box sx={{ ...mntType.labelSm, marginTop: "2px" }}>
                            {Number(m.totalCuYd || 0).toFixed(1)} cu yd · v{m.version} ·{" "}
                            {m.zoneCount} zone{m.zoneCount === 1 ? "" : "s"}
                            {m.clientName ? ` · ${m.clientName}` : ""}
                          </Box>
                        </Box>
                        <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                          <LinkButton
                            label="Open"
                            onClick={() =>
                              navigate(
                                `/mulch-calculator/${woLocationId}/maps/${m.mapGroupId}/edit`,
                              )
                            }
                          />
                          {wo.mulchMap?.mapGroupId === m.mapGroupId ? (
                            <LinkButton
                              label={linkingMap === m.mapGroupId ? "Unlinking…" : "Unlink"}
                              onClick={() => setMapForWo(null, m.mapGroupId)}
                            />
                          ) : (
                            <LinkButton
                              label={linkingMap === m.mapGroupId ? "Linking…" : "Link"}
                              onClick={() => setMapForWo(m.currentMapId, m.mapGroupId)}
                            />
                          )}
                          <BasicButton
                            type="button"
                            scheme="primary"
                            styles={{ margin: "0" }}
                            $submitting={applyingMap === m.mapGroupId}
                            keepText={true}
                            onClick={() => useMapTotal(m)}
                          >
                            Use {Math.round(Number(m.totalCuYd || 0))} yd
                          </BasicButton>
                        </Flex>
                      </Flex>
                    ))
                  )}
                </Box>
              </SectionCard>
            )}

            {/* ── W-b live pricing block, read from the trunk RFQ (A10: Pricing tab) ── */}
            {activeTab === "pricing" && (
            <SectionCard
              title={
                rfq?.sequence != null
                  ? `Pricing · from RFQ GMR-${pad(rfq.sequence)}`
                  : "Pricing"
              }
              action={
                rfqId ? (
                  <LinkButton
                    label="Open RFQ"
                    onClick={() => navigate(`/quote-requests/${rfqId}`)}
                  />
                ) : null
              }
            >
              <Box sx={{ padding: "16px 18px 18px" }}>
                {!rfqId ? (
                  <Box>
                    <Span fontFamily={"Poppins"} fontSize={"13px"} color={mnt.textMuted}>
                      Not priced yet. Generate the RFQ for this work order to price it against the
                      NTE ceiling.
                    </Span>
                    {nteAmount != null && (
                      <Box sx={{ marginTop: "14px" }}>
                        <Field label="NTE ceiling" value={money(nteAmount)} strong />
                      </Box>
                    )}
                  </Box>
                ) : rfqLoading ? (
                  <Box sx={{ textAlign: "center", paddingY: "20px" }}>
                    <Spinner size={20} strokeWidth={3} title="loading" />
                  </Box>
                ) : !pricedService ? (
                  <Box>
                    {ambiguousPricing ? (
                      <>
                        {/* W-m — pick the option here rather than being
                            sent to the RFQ to do it. */}
                        <Span fontFamily={"Poppins"} fontSize={"13px"} color={mnt.textMuted}>
                          The RFQ priced {estimateServices.length} service options. Choose the one
                          this work order should be quoted for.
                        </Span>
                        <Box sx={{ marginTop: "12px" }}>
                          {estimateServices.map((s: any) => {
                            const qty = Number(s?.quantity || 0);
                            const unitPrice = parseFloat(s?.price || 0) || 0;
                            const addons = (s?.addons || []).reduce(
                              (sum: number, a: any) =>
                                sum +
                                (a?.quantity > 0
                                  ? Number(a.quantity) * (parseFloat(a.price || 0) || 0)
                                  : 0),
                              0,
                            );
                            const total = qty * unitPrice + addons;
                            return (
                              <Flex
                                key={s.id}
                                sx={{
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "12px",
                                  padding: "10px 12px",
                                  border: `0.5px solid ${mnt.divider}`,
                                  borderRadius: "6px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Box sx={{ ...mntType.body, fontWeight: 500 }}>
                                    {s?.product?.name || s?.name || "Service option"}
                                  </Box>
                                  <Box sx={{ ...mntType.labelSm, marginTop: "2px" }}>
                                    {s?.name ? `${s.name} · ` : ""}
                                    {qty} {s?.product?.unit || ""} @ {money(unitPrice)}
                                    {s?.depot?.provider?.name ? ` · ${s.depot.provider.name}` : ""}
                                  </Box>
                                </Box>
                                <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                                  <Box sx={{ ...mntType.body, whiteSpace: "nowrap" }}>
                                    {money(total)}
                                  </Box>
                                  <BasicButton
                                    type="button"
                                    scheme="primary"
                                    styles={{ margin: "0" }}
                                    $submitting={selectingService === s.id}
                                    keepText={true}
                                    onClick={() => chooseService(s.id)}
                                  >
                                    Use this
                                  </BasicButton>
                                </Flex>
                              </Flex>
                            );
                          })}
                        </Box>
                      </>
                    ) : (
                      <Span fontFamily={"Poppins"} fontSize={"13px"} color={mnt.textMuted}>
                        The RFQ has no priced service yet. Select services and pricing on the RFQ.
                      </Span>
                    )}
                    {nteAmount != null && (
                      <Box sx={{ marginTop: "14px" }}>
                        <Field label="NTE ceiling" value={money(nteAmount)} strong />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <>
                    {/* W-i — does the priced line answer THIS work order? */}
                    {serviceMatch && serviceMatch.state !== "unknown" && (
                      <Box
                        sx={{
                          background:
                            serviceMatch.state === "match" ? mnt.greenPill : mnt.amberPill,
                          border:
                            serviceMatch.state === "match"
                              ? "none"
                              : `1px solid ${mnt.amberStroke}`,
                          borderRadius: "4px",
                          padding: "7px 10px",
                          marginBottom: "14px",
                        }}
                      >
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"11px"}
                          sx={{
                            color:
                              serviceMatch.state === "match" ? mnt.green : mnt.amber,
                          }}
                        >
                          {serviceMatch.state === "match"
                            ? `Priced for ${serviceMatch.pricedName}, which matches this work order.`
                            : serviceMatch.what === "service"
                            ? `This RFQ is priced for ${serviceMatch.pricedName}, but the work order asks for ${serviceMatch.woName}. Change the service on the work order, or re-price the RFQ, before quoting.`
                            : `This RFQ is priced for ${serviceMatch.pricedName}, but the work order asks for ${serviceMatch.woName}. Change the product on the work order, or re-price the RFQ, before quoting.`}
                        </Span>
                        {/* Resolve: re-price the RFQ to the WO's service.
                            An intentional service change should cascade
                            — offered only for a SERVICE mismatch that is
                            not yet committed (a product mismatch needs
                            RFQ regeneration, not an in-place re-price). */}
                        {serviceMatch.state === "mismatch" &&
                          serviceMatch.what === "service" &&
                          !quotes.some((q: any) => q?.status === 1) &&
                          !attachedProject?.id &&
                          !attachedInvoice?.id && (
                            <Flex
                              sx={{
                                marginTop: "10px",
                                gap: "10px",
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <BasicButton
                                type="button"
                                scheme="primary"
                                styles={{ margin: "0" }}
                                $submitting={repricing}
                                keepText={true}
                                onClick={doForceReprice}
                              >
                                Re-price RFQ for {wo.service?.name || "this service"}
                              </BasicButton>
                              {quotes.length > 0 && (
                                <Span
                                  fontFamily={"Poppins"}
                                  fontSize={"10.5px"}
                                  sx={{ color: mnt.amber }}
                                >
                                  The next quote version will be generated for this service.
                                  The current quote stays live until the new one is accepted.
                                </Span>
                              )}
                            </Flex>
                          )}
                      </Box>
                    )}

                    {/* provider */}
                    <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>Provider</Box>
                        <Box sx={{ ...mntType.body, fontWeight: 500, marginTop: "3px" }}>
                          {pricedService.depot?.provider?.name || "Not resolved"}
                          {pricedService.depot?.name ? ` · ${pricedService.depot.name}` : ""}
                        </Box>
                      </Box>
                    </Flex>

                    <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, margin: "14px 0" }} />

                    {/* priced lines */}
                    {lineItems.map((row, i) => (
                      <Flex
                        key={i}
                        sx={{ justifyContent: "space-between", gap: "16px", marginBottom: "8px" }}
                      >
                        <Box sx={{ ...mntType.body }}>{row.label}</Box>
                        <Box sx={{ ...mntType.body, whiteSpace: "nowrap" }}>{money(row.amount)}</Box>
                      </Flex>
                    ))}

                    {/* RC5 — the trunk provider/price controls, MOUNTED
                        here rather than linked to. Same component the RFQ
                        page renders (`Items.tsx`), so the logic lives
                        once. Hidden once a quote exists: pricing is
                        committed at that point, matching the RFQ page's
                        own gate. `loadRfq` is the required refetch — each
                        write mints a new estimate version. */}
                    {!rfq?.quote && (
                      <Box sx={{ marginTop: "6px", marginLeft: "-20px", marginRight: "-20px" }}>
                        <ProviderPriceControls
                          rfq={rfq}
                          service={pricedService}
                          nteAmount={nteAmount}
                          onChanged={loadRfq}
                        />
                      </Box>
                    )}

                    <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, margin: "14px 0" }} />

                    {/* estimate vs NTE. A11 — NTE is an external-tool
                        (Corrigo/FM) concept; when this work order has no
                        NTE the ceiling column and the over/under pill are
                        both suppressed rather than showing "None set". */}
                    <Flex sx={{ alignItems: "flex-end", gap: "28px", flexWrap: "wrap" }}>
                      <Box>
                        <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>
                          Estimate total
                        </Box>
                        <Box sx={{ ...mntType.valueLg, marginTop: "3px" }}>
                          {money(estimateTotal)}
                        </Box>
                      </Box>
                      {nteAmount != null && (
                        <Box>
                          <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>
                            NTE ceiling
                          </Box>
                          <Box
                            sx={{
                              ...mntType.body,
                              fontWeight: 600,
                              color: mnt.amber,
                              marginTop: "5px",
                            }}
                          >
                            {money(nteAmount)}
                          </Box>
                        </Box>
                      )}
                      {nteDelta != null && (
                        <Box sx={{ marginLeft: "auto" }}>
                          <Box sx={mntPill(nteDelta > 0 ? "overNte" : "atNte")}>
                            {nteDelta > 0
                              ? `Over NTE by ${money(nteDelta)}`
                              : nteDelta === 0
                              ? "At NTE · $0 over"
                              : `Under NTE by ${money(Math.abs(nteDelta))}`}
                          </Box>
                        </Box>
                      )}
                    </Flex>

                    {/* Generate quote, in place on the Pricing tab. The
                        same action as the WhatsNext card, surfaced where
                        the operator is looking at the price. Shown only
                        while pricing is set and no quote exists yet (the
                        provider/price controls above disappear at that
                        point too). */}
                    {pricedService && !rfq?.quote && (
                      <Box sx={{ marginTop: "18px" }}>
                        <BasicButton
                          type="button"
                          scheme="primary"
                          styles={{ margin: "0" }}
                          $submitting={generatingQuote}
                          keepText={true}
                          onClick={generateQuote}
                        >
                          Generate quote
                        </BasicButton>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </SectionCard>
            )}

            {/* ── W-k quote block: the number that goes to the client (A10: Quote tab) ── */}
            {activeTab === "quote" && newestQuote?.id && (
              <SectionCard
                title={
                  newestQuote.sequence != null
                    ? `Quote GMP-${pad(newestQuote.sequence)}`
                    : "Quote"
                }
                action={
                  <LinkButton
                    label="Open quote"
                    onClick={() => navigate(`/quotes/${newestQuote.id}`)}
                  />
                }
              >
                <Box sx={{ padding: "16px 18px 18px" }}>
                  {quoteLoading ? (
                    <Box sx={{ textAlign: "center", paddingY: "16px" }}>
                      <Spinner size={20} strokeWidth={3} title="loading" />
                    </Box>
                  ) : quoteItems.length === 0 ? (
                    <Span fontFamily={"Poppins"} fontSize={"13px"} color={mnt.textMuted}>
                      This quote has no line items yet. Open it to review.
                    </Span>
                  ) : (
                    <>
                      <Flex sx={{ alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                        <Box sx={{ ...mntPill(quoteStatusTone(newestQuote.status)), fontSize: "10.5px" }}>
                          {quoteStatusLabelForWo(newestQuote.status, wo?.status === "rejected")}
                        </Box>
                        <Span fontFamily={"Poppins"} fontSize={"11px"} color={mnt.textMuted}>
                          Generated {fmtDate(quote?.createdAt ?? newestQuote.createdAt)}
                        </Span>
                      </Flex>

                      {quoteItems.map((it: any, i: number) => (
                        <Flex
                          key={it?.id ?? i}
                          sx={{ justifyContent: "space-between", gap: "16px", marginBottom: "8px" }}
                        >
                          <Box sx={{ ...mntType.body, minWidth: 0 }}>
                            {it?.name || "Line item"}
                            {it?.quantity != null ? ` · ${it.quantity} ${it.unit || ""}`.trimEnd() : ""}
                          </Box>
                          <Box sx={{ ...mntType.body, whiteSpace: "nowrap" }}>
                            {money(Number(it?.price ?? 0) * Number(it?.quantity ?? 0))}
                          </Box>
                        </Flex>
                      ))}

                      <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, margin: "14px 0" }} />

                      <Flex sx={{ alignItems: "flex-end", gap: "28px", flexWrap: "wrap" }}>
                        <Box>
                          <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>
                            Quote total
                          </Box>
                          <Box sx={{ ...mntType.valueLg, marginTop: "3px" }}>
                            {money(quoteTotal)}
                          </Box>
                        </Box>
                        {quoteNteDelta != null && (
                          <Box sx={{ marginLeft: "auto" }}>
                            <Box sx={mntPill(quoteNteDelta > 0 ? "overNte" : "atNte")}>
                              {quoteNteDelta > 0
                                ? `Over NTE by ${money(quoteNteDelta)}`
                                : quoteNteDelta === 0
                                ? "At NTE · $0 over"
                                : `Under NTE by ${money(Math.abs(quoteNteDelta))}`}
                            </Box>
                          </Box>
                        )}
                      </Flex>

                      {/* Quote delivery (Session 65 item 3). ONE shared
                          panel with the Email | CorrigoPro toggle, the
                          same component the WO "Send quote" modal mounts,
                          so the embedded surface and the modal cannot
                          drift and the embedded surface now also offers
                          the email option (C2 parity). Default branch
                          follows the plan intake style; a work_order_fed
                          plan opens on CorrigoPro (paste export, mark
                          posted, accept on behalf), a quote_fed plan opens
                          on Email. Shown while the quote is still a live,
                          deliverable version (not denied/expired/
                          superseded 2/4/5); `quote` is the findById-loaded
                          record and onSent/onAccepted refetch it. */}
                      {quote?.id && ![2, 4, 5].includes(Number(newestQuote?.status)) && (
                        <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, marginTop: "16px", paddingTop: "16px" }}>
                          <QuoteDeliveryPanel
                            quoteId={quote.id}
                            quote={quote}
                            intakeStyle={wo?.servicePlan?.intakeStyle}
                            clientId={planClientId}
                            defaultEmail={sendPrefillEmail}
                            defaultCc={newestQuote?.contactsCC ?? null}
                            onPosted={onQuotePosted}
                            onSent={() => {
                              load();
                              loadQuote();
                            }}
                            onAccepted={() => {
                              // Acceptance turns the quote into scheduled
                              // work: reload, refresh the quote, and jump
                              // to the project tab in place (no refresh).
                              load();
                              loadQuote();
                              setActiveTab("project");
                            }}
                          />
                        </Box>
                      )}

                      {/* Written T&C override (Part B) — the quote's terms
                          prose, editable in place. `quote` is findById-
                          loaded, so it carries resolvedTerms + termsOverride. */}
                      {quote?.id && (
                        <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, marginTop: "16px", paddingTop: "16px" }}>
                          <TermsEditor
                            docType="quote"
                            record={quote}
                            update={updateQuote}
                            onSaved={loadQuote}
                          />
                        </Box>
                      )}

                      {/* N3 (pre-acceptance) — Change service type from the
                          QUOTE tab. Consistent with the Project-tab version
                          (build a fresh quote at the new service; the prior
                          quote is superseded only at acceptance). Before
                          acceptance there is no project, so this reuses this
                          surface's own in-place build primitives: point the
                          WO at the new service, select that service's priced
                          estimate line, and generate the fresh quote — no
                          up-front supersede. Shown ONLY while the quote is
                          still live and uncommitted: not accepted (status 1),
                          not declined/expired/superseded, and no project or
                          invoice yet. Requires the WO to name a product (its
                          services drive the picker). */}
                      {user?.accessLevel === "isCompany" &&
                        [0, 3, 6].includes(Number(newestQuote.status)) &&
                        !quotes.some((q: any) => q?.status === 1) &&
                        !attachedProject?.id &&
                        !attachedInvoice?.id &&
                        wo?.product?.id && (
                          <Box
                            sx={{
                              borderTop: `0.5px solid ${mnt.divider}`,
                              marginTop: "16px",
                              paddingTop: "16px",
                            }}
                          >
                            <Box
                              sx={{
                                ...mntType.labelSm,
                                textTransform: "uppercase",
                                marginBottom: "6px",
                              }}
                            >
                              Change service type
                            </Box>
                            <Subtext
                              fontSize={"11px"}
                              fontFamily={"Poppins"}
                              margin={0}
                              marginBottom={"10px"}
                              color={mnt.textMuted}
                            >
                              Re-quote this work order at a different service type. The work order is
                              updated to the new service and a fresh quote is generated for it. The
                              current quote is kept until the new one is accepted, which supersedes it.
                            </Subtext>
                            {!svcChangeOpen ? (
                              <BasicButton
                                type="button"
                                scheme="secondary"
                                styles={{ margin: "0" }}
                                keepText={true}
                                onClick={() => {
                                  setSvcChangeTarget("");
                                  setSvcChangeOpen(true);
                                }}
                              >
                                Change service type
                              </BasicButton>
                            ) : (
                              <Box>
                                <Box sx={{ maxWidth: "420px" }}>
                                  <SelectInput
                                    name="svcChangeTarget"
                                    label="New service type"
                                    value={svcChangeTarget}
                                    options={[
                                      { value: "", label: "Choose a service type…" },
                                      ...(
                                        (products || []).find(
                                          (p: any) => p.id === wo?.product?.id,
                                        )?.services ?? []
                                      )
                                        .filter((s: any) => s.id !== (wo?.service?.id || ""))
                                        .map((s: any) => ({ value: s.id, label: s.name })),
                                    ]}
                                    onChange={(v: any) => setSvcChangeTarget(v || "")}
                                    searchable={false}
                                    clearable={false}
                                  />
                                </Box>
                                <Flex
                                  sx={{
                                    gap: "10px",
                                    alignItems: "center",
                                    marginTop: "12px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <BasicButton
                                    type="button"
                                    scheme="primary"
                                    styles={{ margin: "0" }}
                                    $submitting={svcChanging}
                                    keepText={true}
                                    disabled={!svcChangeTarget}
                                    onClick={changeServiceTypePreAcceptance}
                                  >
                                    Change &amp; generate quote
                                  </BasicButton>
                                  <LinkButton
                                    label="Cancel"
                                    onClick={() => {
                                      setSvcChangeOpen(false);
                                      setSvcChangeTarget("");
                                    }}
                                  />
                                </Flex>
                                <Span
                                  fontFamily={"Poppins"}
                                  fontSize={"10.5px"}
                                  sx={{ color: mnt.amber, display: "block", marginTop: "8px" }}
                                >
                                  A new quote is generated for the new service. The current quote
                                  stays until the new one is accepted, which supersedes it.
                                </Span>
                              </Box>
                            )}
                          </Box>
                        )}
                    </>
                  )}
                </Box>
              </SectionCard>
            )}

            {/* Quote tab: compact VERSION list, so operators can see and
                navigate quote versions from the tab named "Quote" (where
                they look), not only from the Project tab's fuller audit.
                Reuses the same project-scoped history; shows only when a
                project exists AND there are 2+ versions (a single quote is
                not a "history"). Lighter than the Project-tab card: each
                row links to that version; the accept-on-behalf / full audit
                stays on the Project tab. The live version is highlighted. */}
            {activeTab === "quote" &&
              newestQuote?.id &&
              quoteHistory &&
              quoteHistory.length > 1 && (
                <SectionCard title="Quote versions">
                  <Box sx={{ padding: "14px 18px 18px" }}>
                    <Flex sx={{ flexDirection: "column", gap: "8px" }}>
                      {quoteHistory.map((q: any) => {
                        const isRetired = q.status === 5 || !!q.deletedAt;
                        const isCurrent = q.id === newestQuote.id;
                        return (
                          <Flex
                            key={q.id}
                            sx={{
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "12px",
                              padding: "10px 12px",
                              border: `1px solid ${isCurrent ? mnt.link : mnt.divider}`,
                              borderRadius: "8px",
                              background: isRetired ? mnt.bgSubtle : mnt.cardBg,
                              opacity: isRetired ? 0.8 : 1,
                            }}
                          >
                            <Flex sx={{ alignItems: "center", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
                              <Span fontFamily={"Poppins"} fontSize={"12px"} fontWeight={600} sx={{ color: mnt.ink, whiteSpace: "nowrap" }}>
                                GMQ-{pad(q.sequence)}{q.versionDisplay ? ` ${q.versionDisplay}` : ""}
                              </Span>
                              <Box sx={{ ...mntPill(quoteStatusTone(q.status)), fontSize: "10px" }}>
                                {quoteStatusLabelForWo(q.status, wo?.status === "rejected" && isCurrent)}
                              </Box>
                              {isCurrent && (
                                <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.link, whiteSpace: "nowrap" }}>
                                  current
                                </Span>
                              )}
                            </Flex>
                            <Flex sx={{ alignItems: "center", gap: "14px", flexShrink: 0 }}>
                              {q.total != null && (
                                <Span fontFamily={"Poppins"} fontSize={"12px"} sx={{ color: mnt.ink, whiteSpace: "nowrap" }}>
                                  ${Number(q.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Span>
                              )}
                              {!isCurrent && (
                                <LinkButton label="Open" onClick={() => navigate(`/quotes/${q.id}`)} />
                              )}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                    <Subtext
                      fontSize={"11px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginTop={"12px"}
                      color={mnt.textMuted}
                    >
                      The full version audit is on the Project tab.
                    </Subtext>
                  </Box>
                </SectionCard>
              )}

            {/* ── A24 project tab: manage the project in place ── */}
            {activeTab === "project" && attachedProject?.id && (
              <SectionCard
                title={
                  attachedProject.sequence != null
                    ? `Project PRJ-${pad(attachedProject.sequence)}`
                    : "Project"
                }
                action={
                  <LinkButton
                    label="Open project"
                    onClick={() => navigate(`/projects/${attachedProject.id}`)}
                  />
                }
              >
                <Box sx={{ padding: "16px 18px 18px" }}>
                  <Flex sx={{ alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
                    <Box sx={{ ...mntPill("onTrack"), fontSize: "10.5px" }}>
                      {attachedProject.status?.name || "Created"}
                    </Box>
                    {attachedProject.name && (
                      <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                        {attachedProject.name}
                      </Span>
                    )}
                  </Flex>

                  {/* Advance the project through its OWN workflow without
                      leaving the work order. Buttons come from the trunk
                      `/projects/:id/next-status` resolver and write back
                      via `updateProject` — same actions the project page
                      exposes, surfaced here (A24). */}
                  {projectNextSteps && projectNextSteps.length > 0 ? (
                    <>
                      <Box sx={{ ...mntType.labelSm, textTransform: "uppercase", marginBottom: "8px" }}>
                        Next step
                      </Box>
                      <Flex sx={{ gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        {projectNextSteps.map((step: any, i: number) =>
                          isScheduleStep(step) ? (
                            // Session 65 item 7a: the date-bearing
                            // "Scheduled" step is done IN PLACE - set the
                            // date and advance in one action, no trip to
                            // the project edit screen (RC5).
                            <Flex
                              key={i}
                              sx={{ gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}
                            >
                              <Box sx={{ minWidth: "170px" }}>
                                <SelectDate
                                  name="scheduleDate"
                                  label="Scheduled date"
                                  value={scheduleDateInput ?? undefined}
                                  onChange={(val: any) => setScheduleDateInput(val ?? null)}
                                />
                              </Box>
                              <BasicButton
                                type="button"
                                scheme="primary"
                                styles={{ margin: "0" }}
                                $submitting={scheduleSaving}
                                keepText={true}
                                onClick={() => saveScheduledDate(step)}
                              >
                                {step?.transition?.actionText || "Save & schedule"}
                              </BasicButton>
                            </Flex>
                          ) : isSendInvoiceStep(step) ? (
                            // The invoice already exists (raised at project
                            // creation), so this is "send", not "add":
                            // relabel and jump to the invoice tab where the
                            // send button + include pickers live. No create
                            // form (item 5).
                            <BasicButton
                              key={i}
                              type="button"
                              scheme="primary"
                              styles={{ margin: "0" }}
                              keepText={true}
                              onClick={() => setActiveTab("invoice")}
                            >
                              Send invoice
                            </BasicButton>
                          ) : (
                            <BasicButton
                              key={i}
                              type="button"
                              scheme="primary"
                              styles={{ margin: "0" }}
                              $submitting={projectAdvancing}
                              keepText={true}
                              onClick={() => advanceProject(step)}
                            >
                              {step?.transition?.actionText || "Advance"}
                            </BasicButton>
                          ),
                        )}
                      </Flex>
                      <Subtext
                        fontSize={"11px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginTop={"10px"}
                        color={mnt.textMuted}
                      >
                        Advancing here updates the project record directly; the timeline above
                        follows automatically.
                      </Subtext>
                    </>
                  ) : (
                    <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                      No further project step is available from here. Open the project for its full
                      workflow, dispatches, photos and files.
                    </Span>
                  )}

                  {/* N3 - Change service type. A staff shortcut into the
                      project add-quote flow (/projects/:id/add-quote), which
                      auto-prices on product+service select and, on
                      acceptance, runs Phase C reconciliation: the new quote
                      supersedes the current one (retired to the Quote
                      history below), the project's service type flips, and
                      unsent POs / money-free invoices are superseded. The
                      mechanism is already built; this is the affordance +
                      the banner that sets the operator's expectation. */}
                  {user?.accessLevel === "isCompany" && (
                    <Box
                      sx={{
                        marginTop: "16px",
                        paddingTop: "14px",
                        borderTop: `1px solid ${mnt.divider}`,
                      }}
                    >
                      <Box sx={{ ...mntType.labelSm, textTransform: "uppercase", marginBottom: "6px" }}>
                        Change service type
                      </Box>
                      <Subtext
                        fontSize={"11px"}
                        fontFamily={"Poppins"}
                        margin={0}
                        marginBottom={"10px"}
                        color={mnt.textMuted}
                      >
                        Re-quote this project at a different service type. The new quote is priced
                        automatically; accepting it supersedes the current quote (kept in the
                        history below), updates the service type, and retires any unsent PO or
                        unpaid invoice.
                      </Subtext>
                      <BasicButton
                        type="button"
                        scheme="secondary"
                        styles={{ margin: "0" }}
                        keepText={true}
                        onClick={() => navigate(`/projects/${attachedProject.id}/add-quote?returnToWo=${wo?.id}`)}
                      >
                        Change service type
                      </BasicButton>
                    </Box>
                  )}
                </Box>
              </SectionCard>
            )}

            {/* ── Quote history (audit trail): every quote for this project,
                INCLUDING superseded (status 5) and soft-deleted versions,
                newest first. Accepting a re-quote on an existing project
                retires the prior quote(s) to Superseded and links the new
                one back (regeneratedFrom); this is where that chain is
                visible. Staff-only backend read. ── */}
            {activeTab === "project" && attachedProject?.id && quoteHistory && quoteHistory.length > 0 && (
              <SectionCard title="Quote history">
                <Box sx={{ padding: "14px 18px 18px" }}>
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginBottom={"12px"}
                    color={mnt.textMuted}
                  >
                    Every quote version for this project. Accepting a new quote retires the prior
                    one; superseded and archived versions stay here as the record.
                  </Subtext>
                  <Flex sx={{ flexDirection: "column", gap: "8px" }}>
                    {quoteHistory.map((q: any) => {
                      const isRetired = q.status === 5 || !!q.deletedAt;
                      // Only the CURRENT (newest) version reflects a WO
                      // cancel as "Cancelled". Earlier versions were retired
                      // by their successor, not by the cancel, so they keep
                      // their real "Superseded" status - matching the
                      // production Version History panel, which never
                      // rewrites an older version's status.
                      const isCurrentVersion = q.id === newestQuote?.id;
                      return (
                        <Flex
                          key={q.id}
                          sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            padding: "10px 12px",
                            border: `1px solid ${mnt.divider}`,
                            borderRadius: "8px",
                            background: isRetired ? mnt.bgSubtle : mnt.cardBg,
                            opacity: isRetired ? 0.75 : 1,
                          }}
                        >
                          <Flex sx={{ alignItems: "center", gap: "10px", minWidth: 0, flexWrap: "wrap" }}>
                            <Span fontFamily={"Poppins"} fontSize={"12px"} fontWeight={600} sx={{ color: mnt.ink, whiteSpace: "nowrap" }}>
                              GMQ-{pad(q.sequence)}{q.versionDisplay ? ` ${q.versionDisplay}` : ""}
                            </Span>
                            <Box sx={{ ...mntPill(quoteStatusTone(q.status)), fontSize: "10px" }}>
                              {quoteStatusLabelForWo(q.status, wo?.status === "rejected" && isCurrentVersion)}
                            </Box>
                            {q.regeneratedFrom?.id && (
                              <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.textMuted, whiteSpace: "nowrap" }}>
                                replaces {q.regeneratedFrom.versionDisplay || "prior version"}
                              </Span>
                            )}
                          </Flex>
                          <Flex sx={{ alignItems: "center", gap: "14px", flexShrink: 0 }}>
                            {q.total != null && (
                              <Span fontFamily={"Poppins"} fontSize={"12px"} sx={{ color: mnt.ink, whiteSpace: "nowrap" }}>
                                ${Number(q.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Span>
                            )}
                            {q.status === 0 && (
                              <LinkButton
                                label={acceptingQuoteId === q.id ? "Accepting..." : "Accept on behalf"}
                                onClick={() => {
                                  if (acceptingQuoteId) return;
                                  acceptQuoteOnBehalf(q.id);
                                }}
                              />
                            )}
                            <LinkButton label="Open" onClick={() => navigate(`/quotes/${q.id}`)} />
                          </Flex>
                        </Flex>
                      );
                    })}
                  </Flex>
                </Box>
              </SectionCard>
            )}

            {/* ── Invoice tab: the WO's invoice, with its next-step actions
                (Part A, WO_INVOICE_TAB_AND_TERMS.md). Mirrors the PO tab.
                Every action reuses an existing endpoint. ── */}
            {activeTab === "invoice" && attachedInvoice?.id && (
              <SectionCard
                title={
                  attachedInvoice.sequence != null
                    ? `Invoice INV-${pad(attachedInvoice.sequence)}`
                    : "Invoice"
                }
                action={
                  <LinkButton
                    label="Open invoice"
                    onClick={() => navigate(`/invoices/${attachedInvoice.id}`)}
                  />
                }
              >
                <Box sx={{ padding: "16px 18px 18px" }}>
                  <Flex sx={{ alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
                    <Box sx={{ ...mntPill("onTrack"), fontSize: "10.5px" }} title={attachedInvoiceDisplay?.tag?.title}>
                      {attachedInvoiceLabel}
                    </Box>
                    {attachedInvoice.total != null && (
                      <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                        {money(Number(attachedInvoice.total))}
                      </Span>
                    )}
                  </Flex>

                  {/* Next-step actions. Send is offered until the invoice
                      is Sent or Paid; the rest are always available. All
                      reuse endpoints the invoice detail page already uses,
                      so staff advance the invoice without leaving the WO. */}
                  <Box sx={{ ...mntType.labelSm, textTransform: "uppercase", marginBottom: "8px" }}>
                    Next step
                  </Box>
                  <Flex sx={{ gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                    {/* Item 6: the client-email "Send invoice" button is
                        offered on ALL plans, including work_order_fed
                        (Corrigo) - staff explicitly need the option to
                        email the invoice from the WO here, the same as the
                        standalone invoice page. Whether the email actually
                        goes out is decided server-side by the plan's
                        notification config (the notification gate), NOT by
                        hiding the button: a plan configured to email the
                        client sends; one that isn't returns a suppressed
                        result and `sendInvoice` shows the informational
                        "does not email the client" note. Hidden once the
                        invoice has been sent (sentDate set, or status
                        Sent/Paid/Partial Paid). The Corrigo submit panel
                        below remains the primary path on wo_fed. */}
                    {!invoiceAlreadySent && (
                        <BasicButton
                          type="button"
                          scheme="primary"
                          styles={{ margin: "0" }}
                          $submitting={invoiceSending}
                          keepText={true}
                          onClick={() => sendInvoice(attachedInvoice)}
                        >
                          Send invoice
                        </BasicButton>
                      )}
                    <LinkButton
                      label="Record payment"
                      onClick={() =>
                        navigate(`/invoices/${attachedInvoice.id}?tab=payments`)
                      }
                    />
                    <LinkButton
                      label={
                        pdfDownloadingKey === `invoice:${attachedInvoice.id}`
                          ? "Preparing…"
                          : "Download PDF"
                      }
                      onClick={() => {
                        if (pdfDownloadingKey !== `invoice:${attachedInvoice.id}`)
                          downloadInvoicePdf(attachedInvoice);
                      }}
                    />
                  </Flex>

                  {/* Invoice include pickers (photos + site-map snapshots),
                      the SAME surface as the invoice Overview toolbar, so
                      staff choose what prints on the invoice PDF from the
                      work order before Send, without opening the invoice.
                      Snapshot picker self-hides at zero snapshots; photo
                      picker self-hides at zero project photos. Staff only
                      (this whole tab is isCompany). */}
                  {(invoiceSnapshotCount > 0 || projectPhotos.length > 0) && (
                    <Box sx={{ marginTop: "16px" }}>
                      <Box sx={{ ...mntType.labelSm, textTransform: "uppercase", marginBottom: "8px" }}>
                        Include on invoice
                      </Box>
                      <Flex sx={{ gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                        {invoiceSnapshotCount > 0 && invoiceMulchMapId && (
                          <SnapshotIncludePicker
                            recordType="invoice"
                            recordId={attachedInvoice.id}
                            mulchMapId={invoiceMulchMapId}
                            initialSelectedIds={
                              Array.isArray(
                                invoiceFull?.mulchMapSnapshotIds ??
                                  attachedInvoice.mulchMapSnapshotIds,
                              )
                                ? (invoiceFull?.mulchMapSnapshotIds ??
                                    attachedInvoice.mulchMapSnapshotIds)
                                : []
                            }
                          />
                        )}
                        {projectPhotos.length > 0 && attachedProject?.id && (
                          <ProjectPhotoIncludePicker
                            projectId={attachedProject.id}
                            photos={projectPhotos}
                            onChanged={_getProjectPhotos}
                          />
                        )}
                      </Flex>
                    </Box>
                  )}

                  {/* Submit to Corrigo — only on a work_order_fed plan.
                      Mounts the existing submission panel in place. */}
                  {wo?.servicePlan?.intakeStyle === "work_order_fed" && (
                    <Box sx={{ marginTop: "18px" }}>
                      <CorrigoInvoiceSubmissionPanel invoice={attachedInvoice} />
                    </Box>
                  )}

                  {/* Written T&C override (Part B). Uses the findById-
                      loaded invoice (invoiceFull), fetched on tab open, so
                      it carries resolvedTerms + termsOverride. */}
                  {invoiceFull?.id === attachedInvoice.id && (
                    <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, marginTop: "18px", paddingTop: "16px" }}>
                      <TermsEditor
                        docType="invoice"
                        record={invoiceFull}
                        update={updateInvoice}
                        onSaved={loadInvoiceFull}
                      />
                    </Box>
                  )}
                </Box>
              </SectionCard>
            )}

            {/* ── A25 PO tab: the WO's purchase order(s), with send-to-provider ── */}
            {activeTab === "po" && attachedPurchaseOrders.length > 0 && (
              <SectionCard title="Purchase orders">
                <Box sx={{ padding: "12px 16px 14px" }}>
                  {attachedPurchaseOrders.map((po: any, i: number) => {
                    const status = Number(po?.status ?? 0);
                    return (
                      <Box
                        key={po.id ?? i}
                        sx={{
                          paddingY: "12px",
                          borderBottom:
                            i === attachedPurchaseOrders.length - 1
                              ? "none"
                              : `0.5px solid ${mnt.divider}`,
                        }}
                      >
                        <Flex
                          sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <Box sx={{ ...mntType.body, fontWeight: 500 }}>
                                {po.sequence != null ? `PO GMO-${pad(po.sequence)}` : "Purchase order"}
                              </Box>
                              <Box sx={{ ...mntPill(poStatusTone(status)), fontSize: "10px" }}>
                                {poStatusLabel(status)}
                              </Box>
                            </Flex>
                            <Box sx={{ ...mntType.labelSm, marginTop: "3px" }}>
                              {po.provider?.name || "Provider not set"}
                              {po.total != null ? ` · ${money(Number(po.total))}` : ""}
                            </Box>
                          </Box>
                          <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                            {/* The one staff action that advances a PO from
                                here: send a New(0) PO to its provider.
                                Acceptance + payments live on the PO. */}
                            {status === 0 && (
                              <BasicButton
                                type="button"
                                scheme="primary"
                                styles={{ margin: "0" }}
                                $submitting={poSending === po.id}
                                keepText={true}
                                onClick={() => sendPo(po)}
                              >
                                Send to provider
                              </BasicButton>
                            )}
                            {/* Session 65 item 4 - a SENT (1) PO is
                                awaiting the provider. Staff can record the
                                provider's acceptance (or denial) on their
                                behalf here - Corrigo/FM flows and phone
                                confirmations - via the same acceptOrDeny
                                cascade the PO page uses. Accepting moves it
                                to Accepted, where "Submit provider invoice"
                                takes over. */}
                            {status === 1 && (
                              <>
                                <BasicButton
                                  type="button"
                                  scheme="primary"
                                  styles={{ margin: "0" }}
                                  $submitting={poSending === po.id}
                                  keepText={true}
                                  onClick={() => acceptPoOnBehalf(po)}
                                >
                                  {"Accept on provider's behalf"}
                                </BasicButton>
                                <LinkButton
                                  label="Deny"
                                  onClick={() => denyPo(po)}
                                />
                              </>
                            )}
                            {/* Session 65 item 11 - PO close-out in place.
                                Once a PO is Accepted (2) or beyond and is
                                not yet Closed/Cancelled, the close-out step
                                is to record the provider's invoice against
                                it (-> Billed via Zoho on approval, then
                                Closed on payment). Opens the SAME
                                SubmitInvoice modal the PO record uses, in
                                place, so the operator does not leave the WO.
                                The full invoice list/approve stays on the
                                PO record via "Open PO". */}
                            {status >= 2 && status !== 5 && status !== 6 && (
                              <BasicButton
                                type="button"
                                scheme="secondary"
                                styles={{ margin: "0" }}
                                keepText={true}
                                onClick={() => setPoInvoiceModalId(po.id)}
                              >
                                Submit provider invoice
                              </BasicButton>
                            )}
                            <LinkButton
                              label="Open PO"
                              onClick={() => navigate(`/purchase-orders/${po.id}`)}
                            />
                          </Flex>
                        </Flex>

                        {/* Written T&C override (Part B), per PO. Uses the
                            findById-loaded PO (poFull[po.id]), fetched on
                            tab open, so it carries resolvedTerms +
                            termsOverride. Provider-facing terms. */}
                        {poFull[po.id]?.id && (
                          <Box sx={{ marginTop: "12px", paddingTop: "12px", borderTop: `0.5px solid ${mnt.divider}` }}>
                            <TermsEditor
                              docType="po"
                              record={poFull[po.id]}
                              update={updatePurchaseOrder}
                              onSaved={() => loadPoFull(po.id)}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </SectionCard>
            )}

            {/* Mockup 33 (Session 57): the guided next-action card moved
                UP into the header band beside the progress bars. It no
                longer sits at the bottom of the left column — the case's
                one next step reads at eye level, and each tab above
                carries its own record's full controls in place. */}

            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              The work order is the durable record. Generating an RFQ does not consume it: the RFQ
              and quote attach back here, and the stage above is derived from those records, never
              hand-set.
            </Subtext>

            {/* ── W-h plan / batch / site strip ── */}
            <Box
              sx={{
                background: mnt.bgBeige,
                borderRadius: "8px",
                padding: "14px 18px",
              }}
            >
              <Grid gap={"12px 24px"} columns={[1, 3]}>
                <Box>
                  <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>Plan</Box>
                  <Flex sx={{ alignItems: "center", gap: "10px", marginTop: "3px" }}>
                    <Box sx={{ ...mntType.body }}>
                      {planName || "None, one-off work order"}
                    </Box>
                    {wo.servicePlan?.id && (
                      <LinkButton
                        label="Open"
                        onClick={() => navigate(`/maintenance/plans/${wo.servicePlan.id}`)}
                      />
                    )}
                    {/* KNOWN_ISSUES item 20. A WO can end up under the
                        wrong plan through the importer, a cross-plan
                        batch, or split LCPs, and until now there was no
                        way to move it. */}
                    <LinkButton
                      label={wo.servicePlan?.id ? "Move" : "Add to plan"}
                      onClick={() => {
                        setMovePlanId(wo.servicePlan?.id ?? null);
                        setShowMovePlan(true);
                      }}
                    />
                  </Flex>
                </Box>
                <Box>
                  <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>Batch</Box>
                  <Flex sx={{ alignItems: "center", gap: "10px", marginTop: "3px" }}>
                    <Box sx={{ ...mntType.body }}>{batchName || "Unassigned"}</Box>
                    {/* Batch is an organizational fact, not a pricing
                        one — a work order in flight can still be moved to
                        the right cohort, and often needs to be. */}
                    {hasBatches && (
                      <LinkButton
                        label={batchName ? "Change" : "Assign"}
                        onClick={() => {
                          setAssignGroupId(wo.group?.id ?? null);
                          setShowAssign(true);
                        }}
                      />
                    )}
                  </Flex>
                </Box>
                <Box>
                  <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>Site</Box>
                  <Flex sx={{ alignItems: "center", gap: "10px", marginTop: "3px" }}>
                    <Box sx={{ ...mntType.body }}>{siteName || "No matching site"}</Box>
                    {wo.lcp?.location?.id ? (
                      <LinkButton
                        label="Open location"
                        onClick={() => navigate(`/locations/${wo.lcp.location.id}`)}
                      />
                    ) : canMatch ? (
                      <LinkButton label="Match site" onClick={() => setShowMatch(true)} />
                    ) : null}
                  </Flex>
                </Box>
              </Grid>
            </Box>
          </Box>

          {/* ═══ RIGHT RAIL ═══ */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
            {/* ── W-e attached records ── */}
            <SectionCard title="Attached records">
              <Box sx={{ padding: "12px 16px 14px" }}>
                <RailRow
                  dotColor={rfqId ? mnt.green : mnt.divider}
                  title={
                    wo.convertedRfq?.sequence != null
                      ? `RFQ GMR-${pad(wo.convertedRfq.sequence)}`
                      : "RFQ"
                  }
                  sub={
                    rfqId
                      ? pricedService
                        ? nteDelta != null && nteDelta > 0
                          ? "Generated · priced · over NTE"
                          : "Generated · priced"
                        : "Generated · not priced"
                      : "Generate to create"
                  }
                  action={rfqId ? { label: "Open", onClick: () => navigate(`/quote-requests/${rfqId}`) } : null}
                />
                <RailRow
                  dotColor={newestQuote?.id ? mnt.link : mnt.divider}
                  title={
                    newestQuote?.sequence != null ? `Quote GMP-${pad(newestQuote.sequence)}` : "Quote"
                  }
                  sub={newestQuote?.id ? quoteStatusLabelForWo(newestQuote.status, wo?.status === "rejected") : "Not yet"}
                  action={
                    newestQuote?.id
                      ? { label: "Open", onClick: () => navigate(`/quotes/${newestQuote.id}`) }
                      : null
                  }
                  download={
                    newestQuote?.id
                      ? {
                          onClick: () => downloadQuotePdf(newestQuote),
                          downloading: pdfDownloadingKey === `quote:${newestQuote.id}`,
                        }
                      : null
                  }
                />
                <RailRow
                  dotColor={attachedProject?.id ? mnt.green : mnt.divider}
                  title="Project"
                  sub={
                    attachedProject?.id
                      ? attachedProject.status?.name || attachedProject.name || "Created"
                      : "Follows on accept"
                  }
                  action={
                    attachedProject?.id
                      ? { label: "Open", onClick: () => navigate(`/projects/${attachedProject.id}`) }
                      : null
                  }
                />
                <RailRow
                  last={attachedPurchaseOrders.length === 0}
                  dotColor={attachedInvoice?.id ? mnt.green : mnt.divider}
                  title="Invoice"
                  sub={attachedInvoice?.id ? attachedInvoiceLabel : "Follows the project"}
                  action={
                    attachedInvoice?.id
                      ? { label: "Open", onClick: () => navigate(`/invoices/${attachedInvoice.id}`) }
                      : null
                  }
                  download={
                    attachedInvoice?.id
                      ? {
                          onClick: () => downloadInvoicePdf(attachedInvoice),
                          downloading: pdfDownloadingKey === `invoice:${attachedInvoice.id}`,
                        }
                      : null
                  }
                />
                {/* A26 — PO(s) complete the trunk chain on the rail.
                    Rendered only when the WO actually has one; a WO with
                    no PO yet does not show an empty PO row (the chain
                    ends at Invoice until a PO exists). */}
                {attachedPurchaseOrders.map((po: any, i: number) => (
                  <RailRow
                    key={po.id ?? i}
                    last={i === attachedPurchaseOrders.length - 1}
                    dotColor={mnt.green}
                    title={
                      po.sequence != null
                        ? `PO GMO-${pad(po.sequence)}`
                        : "Purchase order"
                    }
                    sub={`${poStatusLabel(Number(po.status ?? 0))}${
                      po.provider?.name ? ` · ${po.provider.name}` : ""
                    }`}
                    action={{ label: "Open", onClick: () => navigate(`/purchase-orders/${po.id}`) }}
                    download={{
                      onClick: () => downloadPoPdf(po),
                      downloading: pdfDownloadingKey === `po:${po.id}`,
                    }}
                  />
                ))}
              </Box>
            </SectionCard>

            {/* ── W-f history ── */}
            <SectionCard title="History">
              <Box sx={{ padding: "14px 16px 16px" }}>
                {historyEvents.length === 0 ? (
                  <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                    Nothing recorded yet.
                  </Span>
                ) : (
                  historyEvents.map((e, i) => (
                    <Flex key={i} sx={{ gap: "10px", marginBottom: i === historyEvents.length - 1 ? 0 : "16px" }}>
                      <Box
                        sx={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: e.tone,
                          flex: "0 0 auto",
                          marginTop: "6px",
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontFamily: "Poppins", fontSize: "10px", color: mnt.textFaint }}>
                          {relTime(e.at)}
                        </Box>
                        <Box sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.ink, marginTop: "2px" }}>
                          {e.title}
                        </Box>
                        {e.actor && (
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "10px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                            }}
                          >
                            {e.actor}
                          </Box>
                        )}
                        {e.sub && (
                          <Box sx={{ fontFamily: "Poppins", fontSize: "10px", color: mnt.textMuted, marginTop: "2px" }}>
                            {e.sub}
                          </Box>
                        )}
                      </Box>
                    </Flex>
                  ))
                )}
              </Box>
            </SectionCard>

            {/* WO comms log — a real comment thread on the case file,
                reusing the trunk polymorphic note service (entityType
                'WorkOrder'). Text comments only for now; file + map
                snapshot attachments are the next layer. */}
            <SectionCard title="Comms log">
              <Box sx={{ padding: "14px 16px 16px" }}>
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add a note to this work order's case file — a call with the FM, a site caveat, an internal heads-up."
                  rows={3}
                  style={{
                    width: "100%",
                    resize: "vertical",
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: mnt.ink,
                    border: `1px solid ${mnt.divider}`,
                    borderRadius: "6px",
                    padding: "8px 10px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <Flex sx={{ justifyContent: "flex-end", marginTop: "8px" }}>
                  <BasicButton
                    type="button"
                    scheme="primary"
                    styles={{ margin: "0" }}
                    $submitting={addingNote}
                    keepText={true}
                    disabled={!noteDraft.trim()}
                    onClick={submitNote}
                  >
                    Add note
                  </BasicButton>
                </Flex>

                <Box sx={{ borderTop: `0.5px solid ${mnt.divider}`, margin: "14px 0 4px" }} />

                {woNotes === null ? (
                  <Box sx={{ textAlign: "center", paddingY: "12px" }}>
                    <Spinner size={18} strokeWidth={3} title="loading" />
                  </Box>
                ) : woNotes.length === 0 ? (
                  <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                    No notes on this work order yet. File and map-snapshot attachments are the next
                    layer of the case file.
                  </Span>
                ) : (
                  woNotes.map((n: any, i: number) => (
                    <Box
                      key={n.id ?? i}
                      sx={{
                        paddingY: "10px",
                        borderBottom:
                          i === woNotes.length - 1 ? "none" : `0.5px solid ${mnt.divider}`,
                      }}
                    >
                      <Flex sx={{ alignItems: "baseline", gap: "8px", marginBottom: "3px" }}>
                        <Box
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            color: mnt.ink,
                          }}
                        >
                          {woNoteAuthor(n)}
                        </Box>
                        <Box sx={{ fontFamily: "Poppins", fontSize: "10px", color: mnt.textFaint }}>
                          {relTime(n.createdAt)}
                        </Box>
                      </Flex>
                      <Box
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          color: mnt.ink,
                          whiteSpace: "pre-wrap",
                          lineHeight: "17px",
                        }}
                      >
                        {n.note}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </SectionCard>
          </Box>
        </Grid>
      </Page>

      {showMatch && (
        <MatchCandidatesModal
          workOrder={wo}
          onClose={() => setShowMatch(false)}
          onMatched={() => {
            setShowMatch(false);
            load();
          }}
        />
      )}

      {showAssign && (
        <BasicModal
          title="Assign to batch"
          cancel_click={() => setShowAssign(false)}
          cancel_text="Cancel"
          size="sm"
          confirm_click={submitAssign}
          confirm_text="Save"
          submitting={assigning}
        >
          <P>
            Work order{" "}
            <strong style={{ color: themevals.colors.black }}>{identity.primary}</strong>
            {planName ? (
              <>
                {" on "}
                <strong style={{ color: themevals.colors.black }}>{planName}</strong>
              </>
            ) : null}
            .
          </P>
          <Box sx={{ marginTop: "12px" }}>
            <BatchRadioList
              groups={planGroups || []}
              value={assignGroupId}
              onChange={setAssignGroupId}
            />
          </Box>
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"10px"}
            color={mnt.textMuted}
          >
            Batches tag work orders, not Locations directly. A Location ends up in a batch when one
            of its WOs is in that batch.
          </Subtext>
        </BasicModal>
      )}

      {showMovePlan && (
        <BasicModal
          title={wo.servicePlan?.id ? "Move to another plan" : "Add to a plan"}
          cancel_click={() => setShowMovePlan(false)}
          cancel_text="Cancel"
          size="sm"
          confirm_click={submitMovePlan}
          confirm_text="Save"
          submitting={movingPlan}
        >
          <P>
            Plans for{" "}
            <strong style={{ color: themevals.colors.black }}>
              {wo.lcp?.client?.name || wo.servicePlan?.client?.name || "this client"}
            </strong>
            . Moving a work order also puts its site on the plan it moves to.
          </P>

          <Box sx={{ marginTop: "12px" }}>
            {clientPlans.length === 0 ? (
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
                This client has no other maintenance plans.
              </Subtext>
            ) : (
              clientPlans.map((p: any) => (
                <Flex
                  key={p.id}
                  onClick={() => setMovePlanId(p.id)}
                  sx={{
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 10px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    background: movePlanId === p.id ? mnt.bgBeige : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    readOnly
                    checked={movePlanId === p.id}
                    name="movePlan"
                  />
                  <Box sx={{ ...mntType.body }}>
                    {p.name}
                    {p.id === wo.servicePlan?.id ? " (current)" : ""}
                  </Box>
                </Flex>
              ))
            )}

            {/* Removing the plan entirely is a legitimate outcome: a WO
                raised against the wrong client's plan, or work that was
                never part of a program. */}
            <Flex
              onClick={() => setMovePlanId(null)}
              sx={{
                alignItems: "center",
                gap: "10px",
                padding: "8px 10px",
                borderRadius: "6px",
                cursor: "pointer",
                background: movePlanId === null ? mnt.bgBeige : "transparent",
              }}
            >
              <input type="radio" readOnly checked={movePlanId === null} name="movePlan" />
              <Box sx={{ ...mntType.body }}>No plan, one-off work order</Box>
            </Flex>
          </Box>

          <Box
            sx={{
              marginTop: "14px",
              padding: "8px 12px",
              background: mnt.amberPill,
              border: `1px solid ${mnt.amberStroke}`,
              borderRadius: "6px",
            }}
          >
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
              {/* Both of these are consequences an operator would
                  otherwise discover afterwards. */}
              Any batch on this work order is cleared, because a batch belongs to a
              single plan. The site stays on the previous plan roster - it may
              still have other work there - so remove it by hand if it should not.
            </Subtext>
          </Box>
        </BasicModal>
      )}

      {showAttachRfq && id && (
        <AttachExistingWorkModal
          mode="workOrder"
          workOrderId={id}
          onClose={() => setShowAttachRfq(false)}
          onDone={() => {
            setShowAttachRfq(false);
            load();
          }}
        />
      )}

      {showQtyConfirm && (
        <BasicModal
          title="Generate RFQ"
          cancel_click={() => setShowQtyConfirm(false)}
          cancel_text="Cancel"
          size="sm"
          confirm_click={confirmGenerateRfq}
          confirm_text="Generate RFQ"
          submitting={generatingRfq}
        >
          <P>
            Confirm the quantity that will price this RFQ. It runs an Instant Estimate for{" "}
            <strong style={{ color: themevals.colors.black }}>
              {wo.product?.name || "the selected product"}
            </strong>{" "}
            at this location and prices both installation and delivery. The RFQ attaches back to
            this work order.
          </P>
          {!wo.product?.id && (
            <Box
              sx={{
                marginTop: "10px",
                padding: "8px 12px",
                background: mnt.amberPill,
                border: `1px solid ${mnt.amberStroke}`,
                borderRadius: "6px",
              }}
            >
              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
                No product is set on this work order. The RFQ will be created unpriced and you will
                pick services on it. Set a product on the work order to price on generate.
              </Subtext>
            </Box>
          )}
          <Box sx={{ marginTop: "14px" }}>
            <TextInput
              type="number"
              name="generateQty"
              label="Quantity"
              min={0}
              value={qtyInput}
              onChange={(val: any) => setQtyInput(val === "" || val == null ? "" : String(val))}
              placeholder="e.g. yards"
            />
          </Box>
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"8px"}
            color={mnt.textMuted}
          >
            A quantity greater than zero is required. If you do not know the volume yet, determine
            it with the mulch calculator.
          </Subtext>
          {canMeasure && (
            <Box sx={{ marginTop: "10px" }}>
              <LinkButton label="Determine with mulch calculator" onClick={goToCalculator} bold />
            </Box>
          )}
        </BasicModal>
      )}

      {showSendQuote && newestQuote?.id && (
        <SendQuoteModal
          quoteId={newestQuote.id}
          quote={quote}
          intakeStyle={wo?.servicePlan?.intakeStyle}
          clientId={planClientId}
          defaultEmail={sendPrefillEmail}
          defaultCc={newestQuote?.contactsCC ?? null}
          initialBranch={sendQuoteBranch}
          onClose={() => setShowSendQuote(false)}
          onSent={() => {
            setShowSendQuote(false);
            load();
            loadQuote();
          }}
        />
      )}

      {/* Session 65 item 11 - PO close-out modal, opened in place from a
          PO row on the WO PO tab. `SubmitInvoice` is the same modal the PO
          record uses; recording the provider invoice drives the PO to
          Billed (Zoho bill on approval). onSuccess reloads the WO so the
          PO row reflects the new state without leaving the case file. */}
      {poInvoiceModalId &&
        (() => {
          const modalPo = attachedPurchaseOrders.find(
            (p: any) => p.id === poInvoiceModalId,
          );
          if (!modalPo) return null;
          return (
            <SubmitInvoice
              po={modalPo}
              isOpen={!!poInvoiceModalId}
              toggle={() => setPoInvoiceModalId(null)}
              onSuccess={() => {
                setPoInvoiceModalId(null);
                load();
              }}
            />
          );
        })()}

      {showCancelConfirm && (
        <BasicModal
          title="Cancel this work order?"
          cancel_click={() => setShowCancelConfirm(false)}
          cancel_text="No, keep it"
          size="sm"
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          confirm_click={submitCancel}
          confirm_text="Yes, cancel it"
          submitting={cancelling}
        >
          <P>
            Cancelling stops this work order from driving active views but keeps its record and
            history{rfqId ? ", and leaves its RFQ and anything priced on it intact" : ""}. Use this
            when the work is not happening — the client withdrew, it is a duplicate, or the site was
            wrong.
          </P>
        </BasicModal>
      )}

      {showDeleteConfirm && (
        <BasicModal
          title="Delete this work order?"
          cancel_click={() => setShowDeleteConfirm(false)}
          cancel_text="No, keep it"
          size="sm"
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          confirm_click={submitDelete}
          confirm_text="Yes, delete"
          submitting={deleting}
        >
          <P>
            This permanently removes work order{" "}
            <strong style={{ color: themevals.colors.black }}>{identity.primary}</strong>
            {rfqId
              ? ", along with the RFQ it generated — nothing has been quoted yet, so nothing is committed to the client"
              : ""}
            . This cannot be undone — to keep a record instead, cancel it.
          </P>
        </BasicModal>
      )}
    </>
  );
}

/* ─── W-a stage strip ─────────────────────────────────────────────── */

/* stage strip removed (A16 replaced it with RecordBarStrip) */

/**
 * Mockup 33 (WO-page redesign, Session 57) — the guided next action,
 * promoted into the HEADER band beside the progress bars instead of
 * sitting at the bottom of the page. ONE primary button: the single next
 * step for the whole case, wherever the live front is. It is a signpost —
 * clicking it performs the step where that is safe in place (generate
 * RFQ / quote), or jumps to the tab where the pivotal work is confirmed
 * (pricing changes, acceptance). The per-tab cards carry each record's
 * FULL control set; this card is just the sharp point.
 *
 * `compact` renders the header variant (fills the former white space to
 * the right of the bars). Null action -> nothing renders (a closed or
 * fully-done case has no next step).
 */
function NextActionCard({
  action,
  poBlocker,
}: {
  action: {
    title: string;
    body: string;
    label: string;
    onClick: () => void;
    submitting?: boolean;
  } | null;
  poBlocker?: { text: string; onClick: () => void } | null;
}) {
  if (!action) return null;
  return (
    <Box
      sx={{
        background: mnt.ink,
        borderRadius: "8px",
        padding: "18px 20px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: "16px",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10.5px",
            fontWeight: 600,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
            color: mnt.onInkMuted,
          }}
        >
          {"What's next"}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            fontWeight: 600,
            color: mnt.onInk,
            marginTop: "8px",
          }}
        >
          {action.title}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "11.5px",
            color: mnt.onInkMuted,
            marginTop: "6px",
            lineHeight: "17px",
          }}
        >
          {action.body}
        </Box>
        {/* PO blocker — a parallel fulfillment concern holding the project
            up, surfaced as a secondary line (NOT the primary step). Amber
            to read as attention, clickable to the PO tab where the send
            action lives. Only present when a PO is actually stuck. */}
        {poBlocker && (
          <Box
            as="button"
            onClick={poBlocker.onClick}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "7px",
              width: "100%",
              textAlign: "left",
              background: "transparent",
              border: "none",
              padding: 0,
              marginTop: "12px",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: mnt.amber,
                flex: "0 0 auto",
                marginTop: "5px",
              }}
            />
            <Box
              sx={{
                fontFamily: "Poppins",
                fontSize: "11px",
                fontWeight: 500,
                color: mnt.onInk,
                lineHeight: "16px",
              }}
            >
              {poBlocker.text}
            </Box>
          </Box>
        )}
      </Box>
      <Box>
        <BasicButton
          type="button"
          scheme="white"
          styles={{ margin: "0" }}
          $submitting={!!action.submitting}
          $loadingColor={mnt.ink}
          keepText={true}
          onClick={action.onClick}
        >
          {action.label}
        </BasicButton>
      </Box>
    </Box>
  );
}


/* ─── small presentational pieces ─────────────────────────────────── */

/**
 * A16 (mockup 32) — each trunk record is its own PROGRESS BAR, read from
 * `wo.stageRecords`. Fill = ordinal / total. Green = complete, blue =
 * live/partial, gray outline = not started, amber = terminal. Clicking a
 * bar jumps to the matching A10 tab.
 */
function RecordBarStrip({
  records,
  closed,
  closedLabel,
  caseClosed,
  hint,
  activeTab,
  onPickTab,
  noMargin,
}: {
  records: any[];
  closed: boolean;
  closedLabel: string;
  caseClosed: boolean;
  hint: string | null;
  activeTab: string;
  onPickTab: (t: string) => void;
  noMargin?: boolean;
}) {
  const tabForKey = (key: string): string =>
    key === "quote"
      ? "quote"
      : key === "project"
      ? "project"
      : key === "invoice"
      ? "invoice"
      : key === "po"
      ? "po"
      : "pricing";

  const colorFor = (state: string) => {
    switch (state) {
      case "complete":
        return { fill: mnt.green, text: mnt.green };
      case "live":
        return { fill: mnt.link, text: mnt.link };
      case "terminal":
        return { fill: mnt.amber, text: mnt.amber };
      default:
        return { fill: mnt.stepTrack, text: mnt.textFaint };
    }
  };

  // mockup 34 (Session 58) — GRADIENT fill. Instead of one solid block,
  // the fill is a row of equal-width slices, one per sub-step of the
  // record's ladder. Each reached slice (index < ordinal) reads a shade
  // of the base color that DARKENS left-to-right (mntStepShade), so the
  // bar shows depth-of-progress within the milestone and its slices match
  // the expand-ladder dots. Unreached slices are transparent (the beige
  // track shows through). `total` is the ladder length; `ordinal` how many
  // rungs are reached. Terminal bars don't use this (they render a solid
  // amber block).
  const renderGradedFill = (base: string, ordinal: number, total: number) => {
    const count = Math.max(1, total);
    return (
      <Flex sx={{ height: "100%", width: "100%" }}>
        {Array.from({ length: count }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: "1 1 0",
              height: "100%",
              background: i < ordinal ? mntStepShade(base, i, count) : "transparent",
              transition: "background 200ms ease",
            }}
          />
        ))}
      </Flex>
    );
  };

  // The expand panel (mockup 32, D4): the ACTIVE bar shows its sub-state
  // ladder by default; clicking any other bar expands THAT one instead.
  // The default expanded bar is the one whose tab is active (the primary
  // active record the A10 default-tab logic already landed on), else the
  // furthest-along started record. Terminal + not_started bars are not
  // auto-expanded but can be clicked open.
  const started = (b: any) => !!b && b.state !== "not_started";
  const activeBarKey =
    records.find((b: any) => started(b) && tabForKey(b.key) === activeTab)?.key ??
    [...records].reverse().find((b: any) => started(b))?.key ??
    null;
  const [expandedKey, setExpandedKey] = useState<string | null>(activeBarKey);
  // Follow the active tab when the operator navigates the strip elsewhere
  // (e.g. a deep link, or advancing a record) so the open panel tracks
  // what they are looking at, without fighting an explicit bar click.
  const lastActiveRef = React.useRef<string | null>(activeBarKey);
  useEffect(() => {
    if (activeBarKey && activeBarKey !== lastActiveRef.current) {
      lastActiveRef.current = activeBarKey;
      setExpandedKey(activeBarKey);
    }
  }, [activeBarKey]);

  const pickBar = (r: any) => {
    if (r?.state === "not_started") return;
    setExpandedKey(r.key);
    onPickTab(tabForKey(r.key));
  };

  const expandedBar =
    records.find((b: any) => b?.key === expandedKey && started(b)) ?? null;

  // mockup 34 rev3 (design/WO_COMPLETION_BAR.md) — RFQ and Quote are
  // one-time GATES: they render as their own standalone bars, unchanged.
  // Project + Invoice + PO are the FULFILLMENT phase (do the work, bill
  // the customer, pay the provider) — they merge into ONE continuous bar
  // where Project is the wide main span and the two closingLeg records
  // (Invoice, PO) are the narrow closing legs in the tail.
  //
  // Split: gates = records with no closingLeg AND not the project;
  // fulfillment = the project bar + the two closingLeg bars, in canonical
  // order. An older payload (no closingLeg on anything, or no project
  // bar) degrades to a flat row of every bar as `gateBars`.
  const isProject = (b: any) => b?.key === "project";
  const gateBars = records.filter((b: any) => !b?.closingLeg && !isProject(b));
  const projectBar = records.find((b: any) => isProject(b)) ?? null;
  const legBars = records.filter((b: any) => !!b?.closingLeg);
  const fulfillmentBars = [projectBar, ...legBars].filter(Boolean);
  const hasFulfillmentBar = !!projectBar || legBars.length > 0;

  // One bar's cell, shared by the spine row and the closing-legs row so
  // the two groups render identically. Reads the same expandedKey/tab
  // state as before; the split is purely where a bar is placed.
  const renderBar = (r: any) => {
    const total = Number(r?.total) || 1;
    const ordinal = Number(r?.ordinal) || 0;
    const pct =
      r?.state === "terminal"
        ? 0
        : Math.max(0, Math.min(100, Math.round((ordinal / total) * 100)));
    const c = colorFor(r?.state);
    const notStarted = r?.state === "not_started";
    const isExpanded = r?.key === expandedKey && !notStarted;
    return (
      <Box
        key={r?.key}
        onClick={() => pickBar(r)}
        sx={{
          flex: "1 1 150px",
          minWidth: "120px",
          cursor: notStarted ? "default" : "pointer",
        }}
      >
        <Box
          sx={{
            height: "18px",
            borderRadius: "9px",
            background: notStarted ? mnt.cardBg : mnt.stepTrack,
            border: notStarted ? `1px solid ${mnt.divider}` : "none",
            overflow: "hidden",
            position: "relative",
            boxShadow: isExpanded ? `0 0 0 2px ${mnt.link}` : "none",
          }}
        >
          {!notStarted && r?.state !== "terminal" && renderGradedFill(c.fill, ordinal, total)}
          {r?.state === "terminal" && (
            <Box sx={{ height: "100%", width: "100%", background: mnt.amber }} />
          )}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "11.5px",
            fontWeight: 600,
            color: isExpanded ? mnt.link : mnt.ink,
            marginTop: "7px",
            textAlign: "center",
          }}
        >
          {r?.label}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10.5px",
            color: c.text,
            marginTop: "1px",
            textAlign: "center",
          }}
        >
          {notStarted
            ? "not started"
            : r?.state === "terminal"
            ? r?.statusLabel
            : `${r?.statusLabel} · ${pct}%`}
        </Box>
      </Box>
    );
  };

  // A SEGMENT inside the continuous fulfillment bar (Project | Invoice |
  // PO). Same click/expand behavior as a gate bar, but rendered as a
  // slice of one shared track rather than its own rounded pill. `flexBasis`
  // weights the Project section wide and the two money legs narrow, so a
  // half-filled Invoice can never out-shout a barely-started Project.
  // `position` controls which end gets rounded (first = left, last =
  // right, middle = square). Fill is the segment's own ordinal/total.
  const renderSegment = (r: any, position: "first" | "middle" | "last", grow: number) => {
    const total = Number(r?.total) || 1;
    const ordinal = Number(r?.ordinal) || 0;
    const pct =
      r?.state === "terminal"
        ? 0
        : Math.max(0, Math.min(100, Math.round((ordinal / total) * 100)));
    const c = colorFor(r?.state);
    const notStarted = r?.state === "not_started";
    const isExpanded = r?.key === expandedKey && !notStarted;
    const leftRadius = position === "first" ? "9px" : "0";
    const rightRadius = position === "last" ? "9px" : "0";
    return (
      <Box
        key={r?.key}
        onClick={() => pickBar(r)}
        sx={{
          flex: `${grow} 1 0`,
          minWidth: 0,
          cursor: notStarted ? "default" : "pointer",
        }}
      >
        <Box
          sx={{
            height: "20px",
            borderTopLeftRadius: leftRadius,
            borderBottomLeftRadius: leftRadius,
            borderTopRightRadius: rightRadius,
            borderBottomRightRadius: rightRadius,
            background: notStarted ? mnt.cardBg : mnt.stepTrack,
            border: notStarted ? `1px solid ${mnt.divider}` : "none",
            // hairline between adjoining segments
            borderRight: position === "last" ? "none" : `2px solid ${mnt.bgBeige}`,
            overflow: "hidden",
            position: "relative",
            boxShadow: isExpanded ? `inset 0 0 0 2px ${mnt.link}` : "none",
          }}
        >
          {!notStarted && r?.state !== "terminal" && renderGradedFill(c.fill, ordinal, total)}
          {r?.state === "terminal" && (
            <Box sx={{ height: "100%", width: "100%", background: mnt.amber }} />
          )}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "11px",
            fontWeight: 600,
            color: isExpanded ? mnt.link : mnt.ink,
            marginTop: "7px",
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {r?.label}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10px",
            color: c.text,
            marginTop: "1px",
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {notStarted
            ? "not started"
            : r?.state === "terminal"
            ? r?.statusLabel
            : `${r?.statusLabel} · ${pct}%`}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        background: mnt.bgBeige,
        borderRadius: "8px",
        padding: "16px 20px 14px",
        marginBottom: noMargin ? 0 : "20px",
        height: "100%",
      }}
    >
      {closed ? (
        <Flex sx={{ alignItems: "center", gap: "12px" }}>
          <Box sx={mntPill("closed")}>{closedLabel}</Box>
          <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
            This work order is closed. No further action is derived from its records.
          </Span>
        </Flex>
      ) : records.length === 0 ? (
        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
          Progress appears here once this work order has records.
        </Span>
      ) : (
        <>
          {/* mockup 34 rev3 (34-milestone-completion-model-rev3.svg) — ONE
              horizontal row: RFQ and Quote are one-time GATES rendered as
              their own fixed-width pills on the LEFT, then an arrow, then
              the merged FULFILLMENT bar continues on the SAME line and
              flex-grows to fill the rest. Project is the wide main span
              (grow 62); Invoice + PO are the narrow closing money legs in
              the tail (grow 19 each), with hairline dividers. Each section
              fills by its OWN ladder and is its own click target. NOT two
              stacked rows — the gates and the fulfillment bar sit side by
              side. A payload with no project bar / no closingLeg degrades
              to just the gate pills. */}
          <Flex sx={{ alignItems: "flex-start", gap: "10px", flexWrap: "wrap" }}>
            {/* gate pills — fixed-ish width, do not grow into the tail */}
            {gateBars.map((r: any) => (
              <Box key={r?.key} sx={{ flex: "0 1 150px", minWidth: "120px" }}>
                {renderBar(r)}
              </Box>
            ))}

            {hasFulfillmentBar && (
              <>
                {/* arrow into the fulfillment bar (mockup) — hidden if there
                    are no gates to point from */}
                {gateBars.length > 0 && (
                  <Box
                    sx={{
                      fontSize: "16px",
                      color: mnt.textFaint,
                      lineHeight: "18px",
                      alignSelf: "flex-start",
                      flex: "0 0 auto",
                    }}
                  >
                    →
                  </Box>
                )}
                {/* the ONE continuous fulfillment bar — takes all remaining
                    width on the same row as the gates */}
                <Flex sx={{ flex: "1 1 320px", minWidth: "260px", alignItems: "flex-start", gap: 0 }}>
                  {fulfillmentBars.map((r: any, i: number) => {
                    const position =
                      i === 0
                        ? "first"
                        : i === fulfillmentBars.length - 1
                        ? "last"
                        : "middle";
                    // Project is the wide main span; the money legs are narrow.
                    const grow = r?.key === "project" ? 62 : 19;
                    return renderSegment(r, position as any, grow);
                  })}
                </Flex>
              </>
            )}
          </Flex>

          {/* Case terminal (mockup 34 Q1). Green only when caseClosed —
              every money leg that exists has settled (Invoice = Paid AND,
              when a PO exists, PO Billed/Closed). Otherwise "open", so a
              case in production with an unpaid leg never reads as done. */}
          <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "14px" }}>
            <Box sx={mntPill(caseClosed ? "current" : "closed")}>
              {caseClosed ? "Case closed" : "Case open"}
            </Box>
            <Span fontFamily={"Poppins"} fontSize={"11px"} color={mnt.textMuted}>
              {caseClosed
                ? "Work complete and every money leg settled."
                : "Closes when the work is done and every money leg is settled."}
            </Span>
          </Flex>

          {/* Expand panel (mockup 32) — the active/clicked bar spells its
              sub-states out from the real enum. Done rungs green, the
              current rung blue, future rungs gray, with a running count. */}
          {expandedBar && Array.isArray(expandedBar.steps) && expandedBar.steps.length > 0 && (
            <RecordStepPanel bar={expandedBar} />
          )}

          {hint && (
            <Box
              sx={{
                fontFamily: "Poppins",
                fontSize: "11px",
                color: mnt.textMuted,
                marginTop: "12px",
              }}
            >
              {hint}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * The expand panel under the active progress bar (mockup 32 / D4). Lists
 * the record's named ladder from the backend `steps`, each rung a dot +
 * label: done (green), current (blue), future (gray). A boxed card with
 * the record title and a "N of M states complete" note, matching the
 * mockup's Project-Scheduled example. Two-column on wide, single on
 * narrow. Terminal records still carry a ladder (marking where they
 * died); the header reads the terminal status so it does not imply
 * forward motion.
 */
function RecordStepPanel({ bar }: { bar: any }) {
  const steps: any[] = Array.isArray(bar?.steps) ? bar.steps : [];
  const doneCount = steps.filter((s) => s?.state === "done").length;
  const isTerminal = bar?.state === "terminal";
  // Base color for the ladder: green on a complete record, blue on a live
  // one, amber on a terminal one. Reached rungs (done/current) read a
  // shade of that base that DARKENS down the ladder (mntStepShade), so a
  // dot matches its slice of the gradient progress bar. Future rungs stay
  // on the neutral track color.
  const rungBase =
    bar?.state === "complete" ? mnt.green : isTerminal ? mnt.amber : mnt.link;
  const count = steps.length;
  const dotColor = (state: string, index: number) => {
    if (state === "done" || state === "current") {
      return isTerminal && state === "current"
        ? mnt.amber
        : mntStepShade(rungBase, index, count);
    }
    return mnt.stepTrack;
  };
  const textColor = (state: string) => {
    switch (state) {
      case "done":
        return mnt.ink;
      case "current":
        return isTerminal ? mnt.amber : mnt.link;
      default:
        return mnt.textFaint;
    }
  };
  const headerTone = isTerminal ? mnt.amber : mnt.link;

  return (
    <Box
      sx={{
        marginTop: "14px",
        background: mnt.cardBg,
        border: `1.5px solid ${headerTone}`,
        borderRadius: "8px",
        padding: "12px 14px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins",
          fontSize: "12px",
          fontWeight: 600,
          color: headerTone,
          marginBottom: "10px",
        }}
      >
        {bar?.label} — {bar?.statusLabel}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr 1fr"],
          columnGap: "18px",
          rowGap: "7px",
        }}
      >
        {steps.map((s: any, i: number) => (
          <Flex key={i} sx={{ alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Box
              sx={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                background: dotColor(s?.state, i),
                flex: "0 0 auto",
              }}
            />
            <Box
              sx={{
                fontFamily: "Poppins",
                fontSize: "11px",
                fontWeight: s?.state === "current" ? 600 : 400,
                color: textColor(s?.state),
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {s?.label}
            </Box>
          </Flex>
        ))}
      </Box>
      {!isTerminal && (
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10.5px",
            color: mnt.textMuted,
            marginTop: "10px",
          }}
        >
          {doneCount} of {steps.length} states complete
        </Box>
      )}
    </Box>
  );
}

/**
 * A10 — the horizontal tab strip over the WO's working area (Maps /
 * Pricing / Quote). Only tabs with content are offered; a status dot on
 * each echoes the matching progress bar.
 */
function WoTabBar({
  active,
  onPick,
  records,
  hasMaps,
  hasQuote,
  hasProject,
  hasInvoice,
  hasPo,
}: {
  active: string;
  onPick: (t: string) => void;
  records: any[];
  hasMaps: boolean;
  hasQuote: boolean;
  hasProject: boolean;
  hasInvoice: boolean;
  hasPo: boolean;
}) {
  const barFor = (key: string) => records.find((r: any) => r?.key === key) ?? null;
  const dotColor = (state: string | undefined) => {
    switch (state) {
      case "complete":
        return mnt.green;
      case "live":
        return mnt.link;
      case "terminal":
        return mnt.amber;
      default:
        return null;
    }
  };

  const tabs: { key: string; label: string; dot: string | null }[] = [];
  if (hasMaps) tabs.push({ key: "maps", label: "Maps", dot: null });
  tabs.push({ key: "pricing", label: "Pricing", dot: dotColor(barFor("rfq")?.state) });
  if (hasQuote) tabs.push({ key: "quote", label: "Quote", dot: dotColor(barFor("quote")?.state) });
  if (hasProject)
    tabs.push({ key: "project", label: "Project", dot: dotColor(barFor("project")?.state) });
  if (hasInvoice)
    tabs.push({ key: "invoice", label: "Invoice", dot: dotColor(barFor("invoice")?.state) });
  if (hasPo) tabs.push({ key: "po", label: "PO", dot: dotColor(barFor("po")?.state) });

  return (
    <Flex
      sx={{
        gap: "4px",
        borderBottom: `1px solid ${mnt.divider}`,
        marginBottom: "-8px",
      }}
    >
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <Box
            key={t.key}
            as="button"
            onClick={() => onPick(t.key)}
            sx={{
              background: "transparent",
              border: "none",
              borderBottom: isActive ? `2px solid ${mnt.link}` : "2px solid transparent",
              padding: "8px 14px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: isActive ? 600 : 400,
              color: isActive ? mnt.link : mnt.textMuted,
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            {t.label}
            {t.dot && (
              <Box
                sx={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: t.dot,
                  flex: "0 0 auto",
                }}
              />
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ ...mntCard }}>
      <Flex
        sx={{
          padding: "12px 18px",
          background: mnt.bgSubtle,
          borderBottom: `0.5px solid ${mnt.divider}`,
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.3px",
            color: mnt.textMuted,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Box>
        {action}
      </Flex>
      {children}
    </Box>
  );
}

function LinkButton({
  label,
  onClick,
  bold,
}: {
  label: string;
  onClick: () => void;
  bold?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        fontSize: "11px",
        fontFamily: "Poppins",
        fontWeight: bold ? 600 : 400,
        color: mnt.link,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

/**
 * Part 5 (PLAN_WO_INHERITANCE.md) — the plan's default product +
 * service type, resolved from serviceTypesConfig (the defaultOnNewWo
 * row, else the first). Used to mark a WO field as an override when it
 * differs. `type` holds a product id; `serviceId` the service type.
 */
function resolvePlanDefaults(
  cfg: any,
): { productId: string | null; serviceId: string | null } {
  const rows = Array.isArray(cfg) ? cfg : [];
  if (rows.length === 0) return { productId: null, serviceId: null };
  const row = rows.find((r: any) => r?.defaultOnNewWo) ?? rows[0];
  return { productId: row?.type || null, serviceId: row?.serviceId || null };
}

function OverridePill() {
  return (
    <Box sx={{ ...mntPill("ungrouped"), fontSize: "9px", padding: "1px 6px" }}>
      Override
    </Box>
  );
}

function Field({
  label,
  value,
  strong,
  link,
  badge,
}: {
  label: string;
  value: string;
  strong?: boolean;
  link?: () => void;
  badge?: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Flex sx={{ alignItems: "center", gap: "6px" }}>
        <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>{label}</Box>
        {badge}
      </Flex>
      {link ? (
        <Box sx={{ marginTop: "3px" }}>
          <LinkButton label={value} onClick={link} />
        </Box>
      ) : (
        <Box
          sx={{
            ...mntType.body,
            fontWeight: strong ? 500 : 400,
            marginTop: "3px",
            wordBreak: "break-word",
          }}
        >
          {value}
        </Box>
      )}
    </Box>
  );
}

function RailRow({
  dotColor,
  title,
  sub,
  action,
  download,
  last,
}: {
  dotColor: string;
  title: string;
  sub: string;
  action?: { label: string; onClick: () => void } | null;
  /** Optional PDF download for this record (Session 58). Rendered as a
   * second link beside the primary action, so staff can pull the
   * record's PDF from the work order without opening it. Offered only
   * on records that have a PDF (Quote / Invoice / PO) and are actually
   * present — a not-yet-created record passes null. `downloading` greys
   * the link + relabels it while the PDF is being generated. */
  download?: { onClick: () => void; downloading?: boolean } | null;
  last?: boolean;
}) {
  return (
    <Box
      sx={{
        paddingY: "10px",
        borderBottom: last ? "none" : `0.5px solid ${mnt.divider}`,
      }}
    >
      <Flex sx={{ alignItems: "center", gap: "10px", justifyContent: "space-between" }}>
        <Flex sx={{ alignItems: "center", gap: "10px", minWidth: 0 }}>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: dotColor,
              flex: "0 0 auto",
            }}
          />
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.ink }}>{title}</Box>
        </Flex>
        {action ? (
          <Flex sx={{ alignItems: "center", gap: "12px", flex: "0 0 auto" }}>
            {download ? (
              <LinkButton
                label={download.downloading ? "Preparing…" : "PDF"}
                onClick={() => {
                  if (!download.downloading) download.onClick();
                }}
              />
            ) : null}
            <LinkButton label={action.label} onClick={action.onClick} />
          </Flex>
        ) : (
          <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: mnt.textFaint }}>{sub}</Box>
        )}
      </Flex>
      {action && (
        <Box
          sx={{
            fontFamily: "Poppins",
            fontSize: "10.5px",
            color: mnt.textMuted,
            marginTop: "3px",
            marginLeft: "16px",
          }}
        >
          {sub}
        </Box>
      )}
    </Box>
  );
}

// A plain radio list for the batch picker inside BasicModal (which has
// no Formik context).
function BatchRadioList({
  groups,
  value,
  onChange,
}: {
  groups: any[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const rows = [{ id: "", name: "Unassigned" }, ...groups];
  return (
    <Box>
      {rows.map((g) => {
        const selected = (value ?? "") === g.id;
        return (
          <button
            key={g.id || "none"}
            type="button"
            onClick={() => onChange(g.id || null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              textAlign: "left",
              background: selected ? mnt.bgBeige : mnt.cardBg,
              border: "1px solid",
              borderColor: mnt.divider,
              borderRadius: "6px",
              padding: "8px 10px",
              marginBottom: "6px",
              fontSize: "12px",
              fontFamily: "Poppins",
              color: mnt.ink,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                border: "2px solid",
                borderColor: selected ? mnt.ink : mnt.textFaint,
                background: selected ? mnt.ink : "transparent",
                flex: "0 0 auto",
              }}
            />
            {g.name}
          </button>
        );
      })}
    </Box>
  );
}

/* ─── formatting helpers ──────────────────────────────────────────── */

function pad(n: number): string {
  return String(n).padStart(5, "0");
}

// Author label for a WO comms note. The note service stamps the actor
// as `lastUpdateBy` on create (createdBy is left null), and findAll
// hydrates `lastUpdateBy.data`, so read the name from there.
function woNoteAuthor(n: any): string {
  const u = n?.lastUpdateBy || n?.createdBy;
  const d = u?.data;
  const name = [d?.firstName, d?.lastName].filter(Boolean).join(" ");
  return name || u?.email || "Staff";
}

// Author label for a recorded activity event. A null createdBy is a
// system action (email intake, an automated re-price), which must read
// as such rather than as an anonymous person.
function eventActor(e: any): string {
  const u = e?.createdBy;
  if (!u) return "System";
  const d = u?.data;
  const name = [d?.firstName, d?.lastName].filter(Boolean).join(" ");
  return name || u?.email || "Staff";
}

// Dot color by event kind. Unknown types (a newer backend than this
// build) fall through to the neutral link tone rather than breaking.
function eventTone(type: string): string {
  switch (type) {
    case "received":
    case "matched":
    case "map_linked":
      return mnt.green;
    case "cancelled":
      return mnt.red;
    case "updated":
    case "repriced":
    case "map_unlinked":
      return mnt.amber;
    default:
      return mnt.link;
  }
}

function money(n: number): string {
  return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/**
 * The cancellation reason a client gave, as ONE readable line.
 *
 * It is TWO fields: a category picked from a list, and optional free
 * text. The first version of this banner printed only the free text, so
 * a request WITH a category read "No reason given" — which told the
 * operator the client had said nothing when they had said "budget".
 * Labels match the client's own picker and the Slack alert.
 */
const CANCELLATION_REASON_LABELS: Record<string, string> = {
  no_longer_needed: "No longer needed",
  budget: "Budget",
  scheduling: "Timing or scheduling",
  duplicate_request: "Duplicate request",
  wrong_site: "Wrong site",
  other: "Something else",
};

function cancellationReasonText(
  reason?: string | null,
  category?: string | null,
): string {
  const label = category
    ? CANCELLATION_REASON_LABELS[category] ?? category
    : null;
  const free = (reason ?? "").trim();
  if (label && free) return `${label} \u2014 ${free}`;
  if (label) return label;
  if (free) return free;
  return "No reason given \u2014 the reason is optional for the client.";
}

function fmtDate(d: any): string {
  return d ? moment(d).format("MMM D, YYYY") : "Not set";
}

function relTime(d: any): string {
  if (!d) return "";
  const then = moment(d);
  const days = moment().diff(then, "days");
  if (days >= 7) return then.format("MMM D, YYYY");
  if (days >= 1) return `${days}d ago`;
  const hours = moment().diff(then, "hours");
  if (hours >= 1) return `${hours}h ago`;
  const minutes = moment().diff(then, "minutes");
  return `${Math.max(minutes, 1)}m ago`;
}

// Trunk Quote.status enum: 0 New · 1 Accepted · 2 Denied · 3 Draft ·
// 4 Expired · 5 Superseded · 6 Price Locked.
function quoteStatusTone(status: number): MntStatus {
  switch (status) {
    case 1:
      return "current";
    case 2:
      return "overdue";
    case 4:
    case 5:
      return "closed";
    case 6:
      return "scheduled";
    default:
      return "woReceived";
  }
}

function quoteStatusLabel(status: number): string {
  switch (status) {
    case 1:
      return "Accepted";
    case 2:
      return "Declined";
    case 3:
      return "Draft";
    case 4:
      return "Expired";
    case 5:
      return "Superseded";
    case 6:
      return "Price locked";
    default:
      return "Ready to send";
  }
}

// A superseded quote (status 5) normally means "a newer version replaced
// this one." But when the WORK ORDER was cancelled, the cancel cascade
// supersedes the quote with NO successor - nothing replaced it, the whole
// job died. "Superseded" mislabels that (superseded by what?). So on a
// cancelled WO, a superseded quote reads "Cancelled" instead. Display-only
// (the stored status stays 5 - the quote enum has no Cancelled value, and
// adding one would touch quote lineage; this relabels what the operator
// sees without a data-model change). Any other status is unchanged.
function quoteStatusLabelForWo(status: number, woCancelled: boolean): string {
  if (woCancelled && status === 5) return "Cancelled";
  return quoteStatusLabel(status);
}

// Trunk PurchaseOrder.status enum, mirroring the PO view's own mapping
// (New / Sent / Accepted / Billed / Denied / Closed / Cancelled). A26 —
// the WO rail shows the PO's live status the same way it shows the
// invoice's.
function poStatusLabel(status: number): string {
  switch (status) {
    case 1:
      return "Sent";
    case 2:
      return "Accepted";
    case 3:
      return "Billed";
    case 4:
      return "Denied";
    case 5:
      return "Closed";
    case 6:
      return "Cancelled";
    default:
      return "New";
  }
}

// A25 — pill tone for a PO status. Denied/Cancelled read terminal
// (overdue tone), Accepted reads live (current), the rest sit on neutral
// scheduled/received tones. Mirrors quoteStatusTone's mapping style.
function poStatusTone(status: number): MntStatus {
  switch (status) {
    case 2:
      return "current";
    case 3:
      return "scheduled";
    case 4:
    case 6:
      return "overdue";
    case 5:
      return "closed";
    case 1:
      return "scheduled";
    default:
      return "woReceived";
  }
}

/**
 * Overflow menu on the WO case-file header: Cancel (soft) and Delete
 * (hard). Each option is a handler or null; a null option is hidden, and
 * the whole menu hides when neither applies (e.g. an already-cancelled
 * WO that also has an RFQ). Bare-bones popover with a document
 * click-outside listener — matches PlanManageMenu; the codebase has no
 * dropdown primitive and a two-item menu doesn't warrant one.
 */
function WoManageMenu({
  onDuplicate,
  onCancel,
  onDelete,
}: {
  onDuplicate: (() => void) | null;
  onCancel: (() => void) | null;
  onDelete: (() => void) | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (!onDuplicate && !onCancel && !onDelete) return null;

  return (
    <Box ref={ref} sx={{ position: "relative", display: "inline-block" }}>
      <BasicButton type="button" outline scheme="secondary" onClick={() => setOpen((v) => !v)}>
        Manage ▾
      </BasicButton>
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: "210px",
            background: "white",
            border: "1px solid",
            borderColor: "inputOutline",
            borderRadius: "6px",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.08)",
            zIndex: 20,
            padding: "6px 0",
          }}
        >
          {onDuplicate && (
            <WoMenuItem
              label="Duplicate work order"
              onClick={() => {
                setOpen(false);
                onDuplicate();
              }}
            />
          )}
          {onCancel && (
            <WoMenuItem
              label="Cancel work order"
              onClick={() => {
                setOpen(false);
                onCancel();
              }}
            />
          )}
          {onDelete && (
            <WoMenuItem
              label="Delete work order"
              danger
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function WoMenuItem({
  label,
  danger,
  onClick,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        padding: "8px 14px",
        cursor: "pointer",
        fontFamily: "Poppins",
        fontSize: "13px",
        color: danger ? themevals.colors.danger : themevals.colors.black,
        "&:hover": { background: "#FAFAF8" },
      }}
    >
      {label}
    </Box>
  );
}
